/*
 * SuperCalc Pro - Advanced Retirement Planning Calculator
 * Copyright © 2024-2025 SuperCalcPro Pty Ltd
 * All Rights Reserved.
 * 
 * PROPRIETARY AND CONFIDENTIAL - TRADE SECRET
 * 
 * This file contains proprietary financial calculation methods, tax optimization
 * algorithms, and regulatory compliance logic that constitute trade secrets.
 * Unauthorized access, copying, distribution, or use is strictly prohibited
 * and may result in legal action.
 * 
 * For licensing inquiries: support@supercalcpro.com.au
 */

// Financial Configuration - Dynamic data loading from JSON scraper
// Data source: /public/data/financial-rates.json (updated by scraper)
// Last updated: September 2026 (Age Pension / deeming auto-switch 20 Sep 2026 AEST)
// Sources: Services Australia, ATO, Treasury

import { FinancialDataLoader } from './FinancialDataLoader.js';
import { resolveFinancialYearRates, australianFinancialYearLabel } from './resolveFinancialYearRates.js';

/** Age Pension / deeming indexation switch (Services Australia, AEST midnight). */
const AGE_PENSION_SEP_2026_SWITCH = new Date('2026-09-20T00:00:00+10:00');
const DEEMING_MAR_2026_SWITCH = new Date('2026-03-20T00:00:00+11:00');
const DEEMING_JUL_2026_THRESHOLDS = new Date('2026-07-01T00:00:00+10:00');
let _ratesJsonRoot = null;

/** Current period: 20 Mar 2026 – 19 Sep 2026 (payment rates); free areas from 1 Jul 2026. */
const AGE_PENSION_UNTIL_SEP_2026 = {
    MAX_PENSION: {
        SINGLE: 28607.80,    // $1,100.30/fn × 26
        COUPLE: 43128.80     // $1,658.80/fn combined × 26
    },
    ASSET_TEST: {
        SINGLE_HOMEOWNER: { FREE_THRESHOLD: 333000, CUT_OFF: 733500 },
        COUPLE_HOMEOWNER: { FREE_THRESHOLD: 499000, CUT_OFF: 1102500 },
        SINGLE_NON_HOMEOWNER: { FREE_THRESHOLD: 600000, CUT_OFF: 1000500 },
        COUPLE_NON_HOMEOWNER: { FREE_THRESHOLD: 766000, CUT_OFF: 1369500 }
    },
    INCOME_TEST: {
        SINGLE: { FREE_THRESHOLD: 5876, CUT_OFF: 68322.80 },   // $226 / $2,627.80 per fn
        COUPLE: { FREE_THRESHOLD: 10296, CUT_OFF: 104436.80 }  // $396 / $4,016.80 per fn
    },
    SUPPLEMENTS: {
        PENSION_SUPPLEMENT: { SINGLE: 2249, COUPLE: 3390.40 }, // $86.50 / $130.40 combined × 26
        ENERGY_SUPPLEMENT: { SINGLE: 366.60, COUPLE: 551.20 }
    },
    RENT_ASSISTANCE: {
        SINGLE: { MAX_PAYMENT: 5512, MIN_RENT: 3120, TAPER_RATE: 0.75 },
        COUPLE: { MAX_PAYMENT: 5195, MIN_RENT: 4160, TAPER_RATE: 0.75 }
    }
};

/** From 20 Sep 2026 indexation (DSS / Services Australia confirmed). */
const AGE_PENSION_FROM_SEP_2026 = {
    MAX_PENSION: {
        SINGLE: 29520.40,    // $1,135.40/fn × 26
        COUPLE: 44506.80     // $1,711.80/fn combined × 26
    },
    ASSET_TEST: {
        SINGLE_HOMEOWNER: { FREE_THRESHOLD: 333000, CUT_OFF: 745750 },
        COUPLE_HOMEOWNER: { FREE_THRESHOLD: 499000, CUT_OFF: 1121000 },
        SINGLE_NON_HOMEOWNER: { FREE_THRESHOLD: 600000, CUT_OFF: 1012750 },
        COUPLE_NON_HOMEOWNER: { FREE_THRESHOLD: 766000, CUT_OFF: 1388000 }
    },
    INCOME_TEST: {
        SINGLE: { FREE_THRESHOLD: 5876, CUT_OFF: 70236.40 },   // $226 / $2,701.40 per fn
        COUPLE: { FREE_THRESHOLD: 10296, CUT_OFF: 107328 }     // $396 / $4,128.00 per fn
    },
    SUPPLEMENTS: {
        PENSION_SUPPLEMENT: { SINGLE: 2293.20, COUPLE: 3458 }, // $88.20 / $133.00 combined × 26
        ENERGY_SUPPLEMENT: { SINGLE: 366.60, COUPLE: 551.20 }
    },
    RENT_ASSISTANCE: {
        // $223.80 / $157.80 single; $211.00 / $255.80 couple (fortnight → annual × 26)
        SINGLE: { MAX_PAYMENT: 5818.80, MIN_RENT: 4102.80, TAPER_RATE: 0.75 },
        COUPLE: { MAX_PAYMENT: 5486, MIN_RENT: 6650.80, TAPER_RATE: 0.75 }
    }
};

function agePensionCoreAsAt(asAt = new Date()) {
    const assessmentDate = new Date(asAt);
    const selected = (Array.isArray(_ratesJsonRoot?.agePensionSchedules)
        ? _ratesJsonRoot.agePensionSchedules
        : [])
        .filter((schedule) => assessmentDate >= new Date(`${schedule.effectiveFrom}T00:00:00+10:00`))
        .sort((a, b) => String(b.effectiveFrom).localeCompare(String(a.effectiveFrom)))[0];
    const canonical = selected || (
        assessmentDate >= DEEMING_JUL_2026_THRESHOLDS ? _ratesJsonRoot?.agePension : null
    );
    if (canonical?.basePension && canonical?.supplements && canonical?.assetTest && canonical?.incomeTest) {
        return {
            MAX_PENSION: {
                SINGLE: canonical.basePension.single,
                COUPLE: canonical.basePension.couple,
            },
            ASSET_TEST: {
                SINGLE_HOMEOWNER: {
                    FREE_THRESHOLD: canonical.assetTest.singleHomeowner.freeThreshold,
                    CUT_OFF: canonical.assetTest.singleHomeowner.cutOff,
                },
                COUPLE_HOMEOWNER: {
                    FREE_THRESHOLD: canonical.assetTest.coupleHomeowner.freeThreshold,
                    CUT_OFF: canonical.assetTest.coupleHomeowner.cutOff,
                },
                SINGLE_NON_HOMEOWNER: {
                    FREE_THRESHOLD: canonical.assetTest.singleNonHomeowner.freeThreshold,
                    CUT_OFF: canonical.assetTest.singleNonHomeowner.cutOff,
                },
                COUPLE_NON_HOMEOWNER: {
                    FREE_THRESHOLD: canonical.assetTest.coupleNonHomeowner.freeThreshold,
                    CUT_OFF: canonical.assetTest.coupleNonHomeowner.cutOff,
                },
            },
            INCOME_TEST: {
                SINGLE: {
                    FREE_THRESHOLD: canonical.incomeTest.single.freeThreshold,
                    CUT_OFF: canonical.incomeTest.single.cutOff,
                },
                COUPLE: {
                    FREE_THRESHOLD: canonical.incomeTest.couple.freeThreshold,
                    CUT_OFF: canonical.incomeTest.couple.cutOff,
                },
            },
            SUPPLEMENTS: {
                PENSION_SUPPLEMENT: {
                    SINGLE: canonical.supplements.pensionSupplement.single,
                    COUPLE: canonical.supplements.pensionSupplement.couple,
                },
                ENERGY_SUPPLEMENT: {
                    SINGLE: canonical.supplements.energySupplement.single,
                    COUPLE: canonical.supplements.energySupplement.couple,
                },
            },
            RENT_ASSISTANCE: {
                SINGLE: {
                    MAX_PAYMENT: canonical.rentAssistance.single.maxPayment,
                    MIN_RENT: canonical.rentAssistance.single.minRent,
                    TAPER_RATE: canonical.rentAssistance.single.taperRate,
                },
                COUPLE: {
                    MAX_PAYMENT: canonical.rentAssistance.couple.maxPayment,
                    MIN_RENT: canonical.rentAssistance.couple.minRent,
                    TAPER_RATE: canonical.rentAssistance.couple.taperRate,
                },
            },
        };
    }
    return asAt >= AGE_PENSION_SEP_2026_SWITCH
        ? AGE_PENSION_FROM_SEP_2026
        : AGE_PENSION_UNTIL_SEP_2026;
}

function deemingAsAt(asAt = new Date()) {
    const assessmentDate = new Date(asAt);
    const selected = (Array.isArray(_ratesJsonRoot?.agePensionSchedules)
        ? _ratesJsonRoot.agePensionSchedules
        : [])
        .filter((schedule) => assessmentDate >= new Date(`${schedule.effectiveFrom}T00:00:00+10:00`))
        .sort((a, b) => String(b.effectiveFrom).localeCompare(String(a.effectiveFrom)))[0];
    const canonical = selected?.deemedIncomeRates || (
        assessmentDate >= DEEMING_JUL_2026_THRESHOLDS
            ? _ratesJsonRoot?.agePension?.deemedIncomeRates
            : null
    );
    if (canonical) {
        return {
            LOW_THRESHOLD: {
                SINGLE: canonical.lowerThreshold,
                COUPLE: canonical.coupleLowerThreshold,
            },
            LOW_RATE: canonical.lowerRate,
            HIGH_RATE: canonical.higherRate,
        };
    }
    const lowThreshold = asAt >= DEEMING_JUL_2026_THRESHOLDS
        ? { SINGLE: 66800, COUPLE: 110600 }
        : { SINGLE: 64200, COUPLE: 106200 };

    if (asAt >= AGE_PENSION_SEP_2026_SWITCH) {
        return { LOW_THRESHOLD: lowThreshold, LOW_RATE: 0.0175, HIGH_RATE: 0.0375 };
    }
    if (asAt >= DEEMING_MAR_2026_SWITCH) {
        return { LOW_THRESHOLD: lowThreshold, LOW_RATE: 0.0125, HIGH_RATE: 0.0325 };
    }
    return { LOW_THRESHOLD: lowThreshold, LOW_RATE: 0.0075, HIGH_RATE: 0.0275 };
}

/** Public helpers for as-at Age Pension / deeming schedules (Services Australia indexation). */
export { agePensionCoreAsAt, deemingAsAt };

// Hardcoded fallback values (only used if JSON fails to load) — current FY 2026-27
const FALLBACK_CONFIG = {
    // Transfer Balance Cap (TBC) - ATO general cap from 1 July 2026
    TRANSFER_BALANCE_CAP: 2100000,

    /** Defined benefit income cap — indexed with TBC (ATO Table 27) */
    DEFINED_BENEFIT_INCOME_CAP: 131250,

    /** Small business CGT cap for super contributions (ATO Table 6) */
    CGT_CAP_AMOUNT: 1935000,

    // Superannuation Tax Rates - ATO
    SUPER_TAX_RATES: {
        CONTRIBUTIONS_TAX: 0.15,        // 15% on concessional contributions
        ACCUMULATION_EARNINGS: 0.15,     // 15% on accumulation earnings
        CGT_DISCOUNT_RATE: 0.10,        // Effective 10% after 1/3 discount on 15%
        WITHDRAWAL_TAX_UNDER_60: 0.15,  // 15% on withdrawals under age 60
        WITHDRAWAL_TAX_OVER_60: 0.00,   // Tax-free withdrawals over age 60
        DIVISION_293_RATE: 0.15         // Additional 15% tax on concessional contributions for high earners
    },

    // Division 293 Tax - ATO (2025-26)
    DIVISION_293: {
        INCOME_THRESHOLD: 250000,       // $250,000 income threshold for Division 293
        TAX_RATE: 0.15                  // Additional 15% tax on concessional contributions above threshold
    },

    // Division 296 (Better Targeted Super Concessions) — enacted March 2026, from 1 July 2026.
    // Treasury Laws Amendment (Building a Stronger and Fairer Super System) Act 2026 (Royal Assent 13 March 2026).
    // Additional tax on Division 296 earnings attributable to TSB above LSBT ($3M for 2026-27, CPI-indexed).
    // Further 10% on earnings proportion above VLSBT ($10M for 2026-27, CPI-indexed).
    DIVISION_296: {
        ENABLED: true,
        THRESHOLD: 3000000,             // Large super balance threshold (LSBT), 2026-27
        VERY_LARGE_THRESHOLD: 10000000, // Very large super balance threshold (VLSBT), 2026-27
        TAX_RATE: 0.15,                 // Additional 15% on earnings proportion above LSBT
        ADDITIONAL_RATE_ABOVE_VLSBT: 0.10, // Further 10% on earnings proportion above VLSBT
        EFFECTIVE_DATE: '2026-07-01',   // First income year: 2026-27
        LSBT_INDEX_INCREMENT: 150000,
        VLSBT_INDEX_INCREMENT: 500000,
        RELEASE_AUTHORITY_LIMIT: 1.0,    // Member can elect release of assessed Div 296 tax from super (confirm ATO rules)
        FIRST_YEAR_FINANCIAL_YEAR: '2026-27', // Transitional TSB test: end of year only
        EARNINGS_METHOD: 'division_296_fund_earnings', // Realised / reported fund earnings (not TSB-movement paper gains)
    },

    // Tax Offsets - ATO (2026-27)
    TAX_OFFSETS: {
        // Low Income Tax Offset (LITO)
        LITO: {
            MAX_OFFSET: 700,            // Maximum offset amount
            THRESHOLD_1: 37500,         // First threshold
            THRESHOLD_2: 45000,         // Second threshold
            THRESHOLD_3: 66667          // Third threshold (offset reduces to zero)
        },
        // Seniors and Pensioners Tax Offset (SAPTO)
        SENIORS: {
            SINGLE: {
                MAX_OFFSET: 2230,       // Maximum offset for single seniors
                THRESHOLD_1: 36034,     // Shade-out threshold
                THRESHOLD_2: 53874      // Cut-out threshold
            },
            COUPLE: {
                MAX_OFFSET: 1602,       // Maximum offset per person for couples
                THRESHOLD_1: 31847,     // Shade-out threshold (per person)
                THRESHOLD_2: 44663      // Cut-out threshold (per person)
            },
            ILLNESS_SEPARATED: {
                MAX_OFFSET: 2040,
                THRESHOLD_1: 34767,
                THRESHOLD_2: 51087
            }
        },
        // Spouse Contribution Tax Offset
        SPOUSE_CONTRIBUTION: {
            MAX_OFFSET: 540,            // Maximum offset amount
            THRESHOLD_1: 37000,         // Spouse income threshold 1
            THRESHOLD_2: 40000          // Spouse income threshold 2 (offset reduces to zero)
        }
    },

    // Superannuation Guarantee and Contribution Caps - ATO (2026-27)
    SUPERANNUATION_GUARANTEE: {
        RATE: 0.12,                     // 12% SG rate (unchanged from 1 July 2025)
        MAXIMUM_QUARTERLY_BASE: 67707.5, // 2026-27 quarterly maximum contribution base
        MINIMUM_THRESHOLD: 450,         // Minimum monthly earnings threshold
        CONTRIBUTIONS_BASE: 32500       // Annual SG contributions base (aligned to concessional cap)
    },

    // Contribution Caps - ATO (2026-27)
    CONTRIBUTION_CAPS: {
        CONCESSIONAL: 32500,            // $32,500 annual concessional contributions cap (2026-27)
        NON_CONCESSIONAL: 130000,       // $130,000 annual non-concessional contributions cap (2026-27)
        CARRY_FORWARD_YEARS: 5,         // Years to carry forward unused concessional cap
        TOTAL_BALANCE_THRESHOLD: 2100000, // $2.1M threshold for non-concessional cap restrictions (matches TBC)
        
        // Government Co-Contribution (2026-27, ATO indexed)
        CO_CONTRIBUTION: {
            MAX_AMOUNT: 500,                    // Maximum government co-contribution
            MIN_INCOME_THRESHOLD: 49293,        // Lower income threshold for max co-contribution
            MAX_INCOME_THRESHOLD: 64293,        // Upper income threshold (no co-contribution above)
            MATCHING_RATE: 0.50,                // 50 cents per dollar
            MAX_ELIGIBLE_CONTRIBUTION: 1000,    // Maximum personal contribution eligible for matching
            AGE_LIMIT: 71,                      // Must be under 71 on 30 June
            WORK_TEST_HOURS: 40                 // 40 hours in 30 consecutive days
        },

        // Low Income Super Tax Offset (LISTO) — no taper; ATI must be <= $37,000
        LISTO: {
            MAX_OFFSET: 500,
            MIN_INCOME_THRESHOLD: 37000,
            MAX_INCOME_THRESHOLD: 37000,
            CONTRIBUTIONS_TAX_RATE: 0.15        // Refund of contributions tax, capped
        },
        
        // Downsizer Contribution
        DOWNSIZER: {
            MIN_AGE: 55,                        // Minimum age (changed from 65 to 55 in 2022)
            MAX_CONTRIBUTION_PER_PERSON: 300000, // $300,000 per person
            MAX_CONTRIBUTION_PER_COUPLE: 600000, // $600,000 per couple
            MIN_OWNERSHIP_YEARS: 10,            // Must have owned for 10+ years
            CONTRIBUTION_DEADLINE_DAYS: 90,     // Must contribute within 90 days of settlement
            EXEMPT_FROM_CAPS: true,             // Not counted against normal caps
            EXEMPT_FROM_WORK_TEST: true,        // No work test required
            EXEMPT_FROM_AGE_LIMIT: true,        // No upper age limit
            EXEMPT_FROM_TSB_TEST: true,         // No TSB test applies
            ONE_TIME_ONLY: true                 // Can only be used once per person
        }
    },

    // Age Thresholds and Preservation Ages - ATO (2025 values)
    AGE_THRESHOLDS: {
        PRESERVATION_AGE: 60,               // Standard preservation age (born after 1 July 1964)
        EARLY_PRESERVATION_AGE: 55,         // For those born before 1 July 1960
        AGE_PENSION_AGE: 67,                // Age Pension eligibility (born after 1 Jan 1957)
        WORK_TEST_AGE: 67,                  // Age when work test applies for contributions
        BRING_FORWARD_MAX_AGE: 74,          // Maximum age for bring-forward rule
        CONTRIBUTION_CUTOFF_AGE: 75,        // Age when contribution rules change
        TAX_FREE_WITHDRAWAL_AGE: 60,        // Age when withdrawals become tax-free
        TRANSITION_TO_RETIREMENT_MIN_AGE: 60 // Minimum age for TTR pensions
    },

    // Shared product defaults — Basic calculator + homepage hero (not legislative rates).
    CALCULATOR_DEFAULTS: {
        /** Years super must last when user leaves horizon blank; aligns with "Typical" scenario (e.g. 65->90). */
        BASIC_RETIREMENT_PERIOD_YEARS: 25,
    },

    // Minimum Pension Standards - ATO (2025 values)
    MINIMUM_PENSION_STANDARDS: {
        // Minimum age to start account-based pension (uses AGE_THRESHOLDS.PRESERVATION_AGE)
        MINIMUM_PENSION_AGE: 60,        // Age 60 is the standard preservation age for most people
        EARLY_ACCESS_AGE: 55,           // Early access with restrictions (birth before 1 July 1960)
        
        // Minimum annual pension withdrawal rates by age
        WITHDRAWAL_RATES: {
            65: 0.05,    // 5% at age 65
            66: 0.05,    // 5% at age 66
            67: 0.05,    // 5% at age 67
            68: 0.05,    // 5% at age 68
            69: 0.05,    // 5% at age 69
            70: 0.05,    // 5% at age 70
            71: 0.05,    // 5% at age 71
            72: 0.05,    // 5% at age 72
            73: 0.05,    // 5% at age 73
            74: 0.05,    // 5% at age 74
            75: 0.06,    // 6% at age 75
            76: 0.06,    // 6% at age 76
            77: 0.06,    // 6% at age 77
            78: 0.06,    // 6% at age 78
            79: 0.06,    // 6% at age 79
            80: 0.07,    // 7% at age 80
            81: 0.07,    // 7% at age 81
            82: 0.07,    // 7% at age 82
            83: 0.07,    // 7% at age 83
            84: 0.07,    // 7% at age 84
            85: 0.09,    // 9% at age 85
            86: 0.09,    // 9% at age 86
            87: 0.09,    // 9% at age 87
            88: 0.09,    // 9% at age 88
            89: 0.09,    // 9% at age 89
            90: 0.11,    // 11% at age 90
            91: 0.11,    // 11% at age 91
            92: 0.11,    // 11% at age 92
            93: 0.11,    // 11% at age 93
            94: 0.11,    // 11% at age 94
            95: 0.14     // 14% at age 95+
        },
        MAXIMUM_WITHDRAWAL_RATE: 0.10   // Maximum 10% withdrawal rate (except age 95+)
    },

    // Work Test Requirements - ATO (2025 values)
    WORK_TEST: {
        AGE_THRESHOLD: 67,              // Work test required from age 67
        MINIMUM_HOURS: 40,              // Minimum 40 hours in 30-day period
        CONCESSIONAL_CONTRIBUTIONS: {
            AGE_THRESHOLD: 75,          // No concessional contributions after age 75
            WORK_TEST_REQUIRED: true    // Work test required for concessional contributions over 67
        },
        NON_CONCESSIONAL_CONTRIBUTIONS: {
            AGE_THRESHOLD: 75,          // No non-concessional contributions after age 75
            WORK_TEST_REQUIRED: true    // Work test required for non-concessional contributions over 67
        }
    },

    // Bring-Forward Rule - ATO (2026-27)
    BRING_FORWARD_RULE: {
        TRIGGER_THRESHOLD: 2100000,     // $2.1M total super balance threshold (matches TBC)
        MAXIMUM_CONTRIBUTION: 390000,   // Maximum $390,000 over 3 years
        YEARS_TO_USE: 3,                // Must use within 3 years
        MINIMUM_CONTRIBUTION: 130000    // Minimum contribution to trigger bring-forward
    },

    // Total Super Balance (TSB) Thresholds - ATO (2026-27)
    TSB_THRESHOLDS: {
        NON_CONCESSIONAL_CUTOFF: 2100000,    // $2.1M - no non-concessional contributions (matches TBC)
        BRING_FORWARD_TRIGGER: 1840000,      // Below this: full 3-year bring-forward available
        BRING_FORWARD_TWO_YEAR: 1970000,     // $1.84M–<$1.97M: two annual caps
        BRING_FORWARD_ONE_YEAR: 2100000,     // $1.97M–<$2.1M: one annual cap
        CARRY_FORWARD_CUTOFF: 500000,        // $500k - no carry-forward of concessional caps
        GOVERNMENT_CO_CONTRIBUTION: 2100000  // Must be below the general transfer balance cap
    },

    // Age Pension — rates/cut-offs/deeming flip on Services Australia indexation dates.
    // Until 19 Sep 2026: March 2026 payment rates + July 2026 free areas / Mar–Aug deeming.
    // From 20 Sep 2026: September 2026 payment rates, cut-offs, and deeming 1.75%/3.75% (auto-switch via getters).
    AGE_PENSION: {
        get MAX_PENSION() { return agePensionCoreAsAt().MAX_PENSION; },
        get ASSET_TEST() { return agePensionCoreAsAt().ASSET_TEST; },
        get INCOME_TEST() { return agePensionCoreAsAt().INCOME_TEST; },
        get SUPPLEMENTS() { return agePensionCoreAsAt().SUPPLEMENTS; },
        get RENT_ASSISTANCE() { return agePensionCoreAsAt().RENT_ASSISTANCE; },
        get DEEMING() { return deemingAsAt(); },

        // Work Bonus - Employment income exemption for Age Pension recipients
        // https://www.servicesaustralia.gov.au/work-bonus
        // Updated: 2025 rules
        WORK_BONUS: {
            ANNUAL_EXEMPTION: 7800,     // $300 per fortnight = $7,800 per year exempt from income test
            MAX_BALANCE: 11800,         // Maximum accrued balance (unused exemption can accumulate)
            ENABLED: true               // Feature flag to enable/disable Work Bonus
        },

        // Pension growth rate (indexation)
        GROWTH_RATE: 0.003  // 0.3% per annum
    },

    // Minimum withdrawal rates - Superannuation Industry (Supervision) Regulations
    MINIMUM_WITHDRAWAL_RATES: {
        UNDER_65: 0.04,    // 4%
        AGE_65_74: 0.05,   // 5%
        AGE_75_79: 0.06,   // 6%
        AGE_80_84: 0.07,   // 7%
        AGE_85_89: 0.09,   // 9%
        AGE_90_94: 0.11,   // 11%
        AGE_95_PLUS: 0.14  // 14%
    },

    // Portfolio and Investment Parameters
    PORTFOLIO: {
        DEFAULT_FRANKING_CREDIT_BOOST: 0.015,  // 1.5% boost for Australian shares
        DEFAULT_PORTFOLIO_TURNOVER: 0.10,      // 10% annual realised turnover assumption
        DEFAULT_DIVIDEND_PORTION: 0.40         // 40% of Australian share returns as dividends
    },

    // Fee Structures (typical AustralianSuper-like structure)
    FEES: {
        DEFAULT_ADMIN_FEE: 52,           // $52 per annum
        DEFAULT_INVESTMENT_FEE: 0.10,    // 0.10% per annum
        DEFAULT_INSURANCE_FEE: 0        // Variable by member
    },

    // Income Tax Brackets - ATO (2026-27) - Loaded from JSON
    INCOME_TAX_BRACKETS: [
        { min: 0, max: 18200, rate: 0 },
        { min: 18200, max: 45000, rate: 0.15 },
        { min: 45000, max: 135000, rate: 0.30 },
        { min: 135000, max: 190000, rate: 0.37 },
        { min: 190000, max: Infinity, rate: 0.45 }
    ],
    
    // Income Tax Rates
    INCOME_TAX: {
        MEDICARE_LEVY: 0.02,
        MEDICARE_LEVY_THRESHOLDS: {
            SINGLE: { LOWER: 28011, UPPER: 35013 },
            SAPTO_SINGLE: { LOWER: 44268, UPPER: 55335 },
            FAMILY: { LOWER: 47238, UPPER: 59047 },
            SAPTO_FAMILY: { LOWER: 61623, UPPER: 77028 },
            DEPENDANT_CHILD_LOWER: 4338,
            DEPENDANT_CHILD_UPPER: 5423
        },
        MEDICARE_LEVY_SURCHARGE: {
            SINGLE: [105000, 123000, 164000],
            FAMILY: [210000, 246000, 328000],
            DEPENDANT_CHILD_INCREMENT: 1500
        }
    },

    // Industry Fund Benchmarks - For comparison purposes (2025 averages)
    INDUSTRY_FUND_BENCHMARKS: {
        FEES: {
            ADMIN_FEE_FIXED: 78,                // Typical fixed admin fee
            ADMIN_FEE_PERCENTAGE: 0.0007,       // 0.07% of balance
            INVESTMENT_FEE_PERCENTAGE: 0.0065, // 0.65% average investment fee
            TYPICAL_TOTAL_FEE: 0.0072,         // 0.72% total average
            INSURANCE_INCLUDED: true            // Usually included by default
        },
        PERFORMANCE: {
            AVERAGE_10_YEAR_RETURN: 7.2,        // 7.2% average annual return (10-year)
            AVERAGE_5_YEAR_RETURN: 6.8,         // 6.8% average annual return (5-year)
            VOLATILITY: 'Medium'                // Managed volatility
        },
        FEATURES: {
            INVESTMENT_OPTIONS: 'Preset',       // Limited to fund offerings
            PROPERTY_ACCESS: false,             // No direct property
            CONTROL_LEVEL: 'Limited',           // Professional management
            CUSTOMIZATION: 'Low'                // Standard packages
        }
    },

    // SMSF-Specific Configuration - ATO (2025 values)
    SMSF: {
        // SMSF Fee Structures - ATO Guidelines (2025)
        FEES: {
            // Trustee Fees (annual)
            TRUSTEE_FEES: {
                INDIVIDUAL: {
                    FIXED: 500,              // Minimum fixed fee for individual trustees
                    PERCENTAGE: 0.0025,      // 0.25% of balance (typical range 0.1%-0.5%)
                    MAXIMUM: 5000            // Maximum reasonable trustee fee
                },
                CORPORATE: {
                    FIXED: 800,              // Minimum fixed fee for corporate trustee
                    PERCENTAGE: 0.003,       // 0.30% of balance (typical range 0.15%-0.6%)
                    MAXIMUM: 8000            // Maximum reasonable corporate trustee fee
                }
            },
            
            // Annual Audit Costs - ATO Requirements
            AUDIT_COSTS: {
                STANDARD: 1200,              // Standard SMSF audit (typical range $800-$2000)
                COMPLEX: 2500,               // Complex SMSF audit (with LRBA, collectibles, etc.)
                MINIMUM: 600,                // Minimum reasonable audit cost
                MAXIMUM: 5000                // Maximum reasonable audit cost
            },
            
            // Compliance and Administration Costs
            COMPLIANCE_COSTS: {
                ANNUAL_RETURN: 400,          // ATO annual return preparation
                BOOKKEEPING: 800,            // Annual bookkeeping (typical range $500-$1500)
                ADMINISTRATION: 600,         // General administration costs
                SOFTWARE_LICENSES: 300       // SMSF software licenses
            },
            
            // Investment Management Fees (varies by asset type)
            INVESTMENT_MANAGEMENT: {
                DIRECT_SHARES: {
                    BROKERAGE: 0.0015,       // 0.15% brokerage (typical range 0.1%-0.3%)
                    RESEARCH: 0.002,         // 0.2% research and analysis
                    MAXIMUM_PER_TRADE: 50    // Maximum brokerage per trade
                },
                MANAGED_FUNDS: {
                    MANAGEMENT_FEE: 0.012,   // 1.2% management fee (typical range 0.8%-2.0%)
                    PERFORMANCE_FEE: 0.15,   // 15% performance fee (if applicable)
                    BUY_SELL_SPREAD: 0.005   // 0.5% buy/sell spread
                },
                PROPERTY: {
                    MANAGEMENT_FEE: 0.07,    // 7% property management fee
                    MAINTENANCE_RESERVE: 0.02, // 2% maintenance reserve
                    VALUATION_COST: 800      // Annual property valuation
                },
                CASH_TERM_DEPOSITS: {
                    MANAGEMENT_FEE: 0.001,   // 0.1% management fee
                    TRANSACTION_COST: 25     // Transaction costs
                }
            },
            
            // Insurance Costs (annual)
            INSURANCE: {
                LIFE_INSURANCE: {
                    BASE_RATE: 0.003,        // 0.3% of sum insured (varies by age/health)
                    MINIMUM_SUM: 100000,     // Minimum $100k sum insured
                    MAXIMUM_SUM: 2000000     // Maximum $2M sum insured (practical limit)
                },
                TPD_INSURANCE: {
                    BASE_RATE: 0.002,        // 0.2% of sum insured
                    MINIMUM_SUM: 100000,
                    MAXIMUM_SUM: 1500000
                },
                INCOME_PROTECTION: {
                    BASE_RATE: 0.005,        // 0.5% of annual income
                    WAITING_PERIOD: 30,      // 30-day waiting period
                    BENEFIT_PERIOD: 2        // 2-year benefit period
                }
            }
        },

        // SMSF Borrowing Rules - ATO (2025)
        BORROWING: {
            // Limited Recourse Borrowing Arrangements (LRBA)
            LRBA: {
                MAXIMUM_LOAN_TO_VALUE: 0.70,  // 70% maximum LVR
                MINIMUM_DEPOSIT: 0.30,         // 30% minimum deposit
                MAXIMUM_LOAN_TERM: 30,         // 30 years maximum loan term
                SINGLE_ACQUISITION_ONLY: true, // Can only borrow for single asset acquisition
                REPAIRS_MAINTENANCE_ALLOWED: true, // Repairs and maintenance allowed
                IMPROVEMENTS_NOT_ALLOWED: true,    // Improvements not allowed while borrowed
                RELATED_PARTY_RESTRICTIONS: true   // Restrictions on related party loans
            },
            
            // Related Party Loan Rules
            RELATED_PARTY_LOANS: {
                MAXIMUM_INTEREST_RATE: 0.12,  // 12% maximum interest rate (commercial rate)
                MINIMUM_INTEREST_RATE: 0.04,  // 4% minimum interest rate
                SECURITY_REQUIRED: true,      // Security required for related party loans
                DOCUMENTATION_REQUIRED: true, // Full loan documentation required
                REGULAR_REPAYMENTS: true      // Regular repayments required
            },
            
            // Borrowing Restrictions
            RESTRICTIONS: {
                NO_BORROWING_FROM_MEMBERS: true,     // Cannot borrow from members
                NO_BORROWING_FOR_PERSONAL_USE: true, // Cannot borrow for personal use
                NO_GEARED_INVESTMENTS_ALLOWED: true, // No geared investments allowed
                NO_SECURITY_PROVIDED_TO_THIRD_PARTIES: true // Cannot provide security to third parties
            }
        },

        // SMSF Asset Valuation Guidelines - ATO (2025)
        VALUATION: {
            // Valuation Requirements
            REQUIREMENTS: {
                ANNUAL_VALUATION_REQUIRED: true,     // Annual valuation required
                PROFESSIONAL_VALUATION_THRESHOLD: 1000000, // $1M threshold for professional valuation
                VALUATION_DATE: '30_JUNE',           // Valuation date for financial year
                DOCUMENTATION_REQUIRED: true         // Valuation documentation required
            },
            
            // Asset-Specific Valuation Rules
            REAL_ESTATE: {
                PROFESSIONAL_VALUER_REQUIRED: true,  // Professional valuer required
                VALUATION_COST_RANGE: {
                    MINIMUM: 600,
                    MAXIMUM: 2000,
                    TYPICAL: 800
                },
                MARKET_EVIDENCE_REQUIRED: true,      // Market evidence required
                VALUATION_FREQUENCY: 'ANNUAL'        // Annual valuation required
            },
            
            COLLECTIBLES: {
                PROFESSIONAL_VALUER_REQUIRED: true,  // Professional valuer required
                VALUATION_COST_RANGE: {
                    MINIMUM: 300,
                    MAXIMUM: 1000,
                    TYPICAL: 500
                },
                INSURANCE_REQUIRED: true,            // Insurance required for collectibles
                STORAGE_REQUIREMENTS: true,          // Specific storage requirements
                DISPOSAL_RESTRICTIONS: true          // Restrictions on disposal to related parties
            },
            
            SHARES_BONDS: {
                MARKET_VALUE_REQUIRED: true,         // Market value required
                VALUATION_SOURCE: 'ASX_MARKET',      // ASX market prices
                VALUATION_FREQUENCY: 'REAL_TIME',    // Real-time valuation
                NO_VALUATION_COST: true              // No additional valuation cost
            }
        },

        // SMSF Compliance Rules - ATO (2025)
        COMPLIANCE: {
            // Sole Purpose Test
            SOLE_PURPOSE_TEST: {
                PRIMARY_PURPOSE: 'RETIREMENT_BENEFITS', // Primary purpose: retirement benefits
                BENEFICIARY_RESTRICTIONS: true,         // Limited to members and dependents
                INVESTMENT_RESTRICTIONS: true,          // Investment restrictions apply
                EXPENSE_RESTRICTIONS: true              // Expense restrictions apply
            },
            
            // In-House Asset Rules
            IN_HOUSE_ASSETS: {
                MAXIMUM_PERCENTAGE: 0.05,               // 5% maximum of total assets
                DEFINITION_INCLUDES: [
                    'RELATED_PARTY_LOANS',
                    'RELATED_PARTY_LEASES',
                    'RELATED_PARTY_INVESTMENTS'
                ],
                PENALTIES_APPLY: true,                  // Penalties apply for breaches
                CORRECTION_REQUIRED: true               // Immediate correction required
            },
            
            // Related Party Transaction Rules
            RELATED_PARTY_TRANSACTIONS: {
                COMMERCIAL_TERMS_REQUIRED: true,       // Commercial terms required
                DOCUMENTATION_REQUIRED: true,          // Full documentation required
                ARM_LENGTH_PRINCIPLE: true,            // Arm's length principle applies
                REGULAR_MONITORING: true               // Regular monitoring required
            },
            
            // Investment Strategy Requirements
            INVESTMENT_STRATEGY: {
                WRITTEN_STRATEGY_REQUIRED: true,       // Written strategy required
                REGULAR_REVIEW_REQUIRED: true,         // Regular review required
                DIVERSIFICATION_CONSIDERATION: true,   // Diversification consideration required
                RISK_RETURN_CONSIDERATION: true,       // Risk/return consideration required
                LIQUIDITY_CONSIDERATION: true,         // Liquidity consideration required
                MEMBER_CIRCUMSTANCES: true             // Member circumstances consideration required
            }
        },

        // SMSF Administration Requirements - ATO (2025)
        ADMINISTRATION: {
            // Record Keeping Requirements
            RECORD_KEEPING: {
                MINIMUM_RETENTION_PERIOD: 10,          // 10 years minimum retention
                DOCUMENT_TYPES: [
                    'FINANCIAL_STATEMENTS',
                    'BANK_STATEMENTS',
                    'INVESTMENT_RECORDS',
                    'CONTRIBUTION_RECORDS',
                    'PENSION_RECORDS',
                    'TRANSACTION_RECORDS'
                ],
                ELECTRONIC_RECORDS_ALLOWED: true,      // Electronic records allowed
                BACKUP_REQUIRED: true                  // Backup required
            },
            
            // Annual Return Requirements
            ANNUAL_RETURN: {
                LODGEMENT_DUE_DATE: '28_FEBRUARY',     // Due date for lodgement
                LATE_LODGEMENT_PENALTY: 222,           // $222 per 28 days late
                MAXIMUM_PENALTY: 1110,                 // Maximum penalty $1,110
                PROFESSIONAL_PREPARATION_RECOMMENDED: true // Professional preparation recommended
            },
            
            // Trustee Education Requirements
            TRUSTEE_EDUCATION: {
                MANDATORY_EDUCATION: false,            // Not currently mandatory
                RECOMMENDED_EDUCATION: true,           // Education recommended
                ATO_RESOURCES_AVAILABLE: true,         // ATO resources available
                CONTINUING_EDUCATION_RECOMMENDED: true // Continuing education recommended
            }
        }
    }
};

export function resolveFinancialConfigAsAt(asAt = null) {
    if (!asAt || !_ratesJsonRoot?.schedules?.length) return FinancialConfig;
    return mapJSONToConfig(resolveFinancialYearRates(_ratesJsonRoot, asAt));
}

// Helper function to calculate income tax using the FY active at asAt.
export function calculateIncomeTax(grossIncome, asAt = null) {
    const brackets = resolveFinancialConfigAsAt(asAt).INCOME_TAX_BRACKETS;
    
    if (grossIncome <= brackets[0].max) {
        return 0; // Tax-free threshold
    }
    
    let tax = 0;
    for (let i = 1; i < brackets.length; i++) {
        const bracket = brackets[i];
        const prevBracket = brackets[i - 1];
        
        if (grossIncome <= prevBracket.max) {
            break; // Income is below this bracket
        }
        
        const taxableInBracket = Math.min(grossIncome, bracket.max) - prevBracket.max;
        tax += taxableInBracket * bracket.rate;
        
        if (grossIncome <= bracket.max) {
            break; // Income doesn't exceed this bracket
        }
    }
    
    return tax;
}

// Helper function to calculate after-tax income
export function calculateAfterTaxIncome(grossIncome, asAt = null) {
    const tax = calculateIncomeTax(grossIncome, asAt);
    return grossIncome - tax;
}

/**
 * Medicare levy with the statutory low-income exemption and shade-in.
 * Family reductions require familyIncome; otherwise the individual thresholds apply.
 */
export function calculateMedicareLevy(taxableIncome, options = {}) {
    const income = Math.max(0, Number(taxableIncome) || 0);
    const {
        familyIncome = null,
        dependantChildren = 0,
        saptoEligible = false,
        asAtDate = null,
    } = options;
    const taxConfig = resolveFinancialConfigAsAt(asAtDate);
    const cfg = taxConfig.INCOME_TAX.MEDICARE_LEVY_THRESHOLDS;
    const useFamily = familyIncome != null && Number.isFinite(Number(familyIncome));
    const childCount = Math.max(0, Math.floor(Number(dependantChildren) || 0));
    const base = useFamily
        ? (saptoEligible ? cfg.SAPTO_FAMILY : cfg.FAMILY)
        : (saptoEligible ? cfg.SAPTO_SINGLE : cfg.SINGLE);
    const lower = base.LOWER + (useFamily ? childCount * cfg.DEPENDANT_CHILD_LOWER : 0);
    const upper = base.UPPER + (useFamily ? childCount * cfg.DEPENDANT_CHILD_UPPER : 0);
    const testIncome = useFamily ? Math.max(0, Number(familyIncome) || 0) : income;

    if (testIncome <= lower || income <= 0) return 0;
    if (testIncome >= upper) return income * taxConfig.INCOME_TAX.MEDICARE_LEVY;
    return Math.min(
        income * taxConfig.INCOME_TAX.MEDICARE_LEVY,
        (testIncome - lower) * 0.10
    );
}

/** Medicare levy surcharge for a full year without appropriate hospital cover. */
export function calculateMedicareLevySurcharge(incomeForSurcharge, options = {}) {
    const income = Math.max(0, Number(incomeForSurcharge) || 0);
    const {
        isFamily = false,
        dependantChildrenAfterFirst = 0,
        hasPrivateHealthInsurance = true,
        asAtDate = null,
    } = options;
    if (hasPrivateHealthInsurance || income <= 0) return 0;

    const cfg = resolveFinancialConfigAsAt(asAtDate).INCOME_TAX.MEDICARE_LEVY_SURCHARGE;
    const increment = isFamily
        ? Math.max(0, Math.floor(Number(dependantChildrenAfterFirst) || 0)) * cfg.DEPENDANT_CHILD_INCREMENT
        : 0;
    const [tier1, tier2, tier3] = (isFamily ? cfg.FAMILY : cfg.SINGLE).map((v) => v + increment);
    if (income > tier3) return income * 0.015;
    if (income > tier2) return income * 0.0125;
    if (income > tier1) return income * 0.01;
    return 0;
}

/** Resident income tax, non-refundable offsets, Medicare levy and optional MLS. */
export function calculatePersonalIncomeTax(taxableIncome, options = {}) {
    const income = Math.max(0, Number(taxableIncome) || 0);
    const {
        age = 0,
        isCouple = false,
        illnessSeparated = false,
        familyIncome = null,
        dependantChildren = 0,
        hasPrivateHealthInsurance = true,
        applySapto = false,
        asAtDate = null,
    } = options;
    const baseTax = calculateIncomeTax(income, asAtDate);
    const lito = calculateLITO(income, asAtDate);
    const sapto = applySapto
        ? calculateSeniorsTaxOffset(income, isCouple, age, illnessSeparated, asAtDate)
        : 0;
    const offsets = Math.min(baseTax, lito + sapto);
    const incomeTax = Math.max(0, baseTax - offsets);
    const medicareLevy = calculateMedicareLevy(income, {
        familyIncome,
        dependantChildren,
        saptoEligible: applySapto && age >= FinancialConfig.AGE_THRESHOLDS.AGE_PENSION_AGE,
        asAtDate,
    });
    const medicareLevySurcharge = calculateMedicareLevySurcharge(
        familyIncome == null ? income : familyIncome,
        {
            isFamily: isCouple || familyIncome != null,
            dependantChildrenAfterFirst: Math.max(0, dependantChildren - 1),
            hasPrivateHealthInsurance,
            asAtDate,
        }
    );
    return {
        totalTax: incomeTax + medicareLevy + medicareLevySurcharge,
        baseTax,
        lito,
        sapto,
        incomeTax,
        medicareLevy,
        medicareLevySurcharge,
    };
}

// Helper function to get minimum withdrawal rate by age
export function getMinimumWithdrawalRate(age) {
    const rates = FinancialConfig.MINIMUM_WITHDRAWAL_RATES;
    
    if (age < 65) return rates.UNDER_65;
    if (age < 75) return rates.AGE_65_74;
    if (age < 80) return rates.AGE_75_79;
    if (age < 85) return rates.AGE_80_84;
    if (age < 90) return rates.AGE_85_89;
    if (age < 95) return rates.AGE_90_94;
    return rates.AGE_95_PLUS;
}

// Helper function to get Age Pension parameters
export function getAgePensionParams(isCouple, isHomeowner, inflationMultiplier = 1, growthMultiplier = 1, asAt = null) {
    const assessmentDate = asAt ? new Date(asAt) : new Date();
    const core = agePensionCoreAsAt(assessmentDate);
    const deeming = deemingAsAt(assessmentDate);
    
    const base = {
        maxPension: isCouple ? core.MAX_PENSION.COUPLE : core.MAX_PENSION.SINGLE,
        assetFreeThreshold: isCouple 
            ? (isHomeowner ? core.ASSET_TEST.COUPLE_HOMEOWNER.FREE_THRESHOLD : core.ASSET_TEST.COUPLE_NON_HOMEOWNER.FREE_THRESHOLD)
            : (isHomeowner ? core.ASSET_TEST.SINGLE_HOMEOWNER.FREE_THRESHOLD : core.ASSET_TEST.SINGLE_NON_HOMEOWNER.FREE_THRESHOLD),
        assetCutOff: isCouple 
            ? (isHomeowner ? core.ASSET_TEST.COUPLE_HOMEOWNER.CUT_OFF : core.ASSET_TEST.COUPLE_NON_HOMEOWNER.CUT_OFF)
            : (isHomeowner ? core.ASSET_TEST.SINGLE_HOMEOWNER.CUT_OFF : core.ASSET_TEST.SINGLE_NON_HOMEOWNER.CUT_OFF),
        incomeFreeThreshold: isCouple ? core.INCOME_TEST.COUPLE.FREE_THRESHOLD : core.INCOME_TEST.SINGLE.FREE_THRESHOLD,
        incomeCutOff: isCouple ? core.INCOME_TEST.COUPLE.CUT_OFF : core.INCOME_TEST.SINGLE.CUT_OFF,
        lowDeemThresh: isCouple ? deeming.LOW_THRESHOLD.COUPLE : deeming.LOW_THRESHOLD.SINGLE,
        lowDeemRate: deeming.LOW_RATE,
        highDeemRate: deeming.HIGH_RATE,
        pensionSupplement: isCouple ? core.SUPPLEMENTS.PENSION_SUPPLEMENT.COUPLE : core.SUPPLEMENTS.PENSION_SUPPLEMENT.SINGLE,
        energySupplement: isCouple ? core.SUPPLEMENTS.ENERGY_SUPPLEMENT.COUPLE : core.SUPPLEMENTS.ENERGY_SUPPLEMENT.SINGLE,
    };
    
    // Apply multipliers to monetary values (not rates)
    return Object.keys(base).reduce((acc, key) => {
        if (key !== 'lowDeemRate' && key !== 'highDeemRate') {
            acc[key] = base[key] * inflationMultiplier * growthMultiplier;
        } else {
            acc[key] = base[key];
        }
        return acc;
    }, {});
}

/**
 * Age Pension params for projections that use a real (above-CPI) pension growth rate.
 * Statute: asset/income free areas and deeming thresholds index to CPI (flat in real dollars).
 * The maximum rate can rise faster (CPI / PBLCI / MTAWE); part-pension cut-offs move with the rate.
 * So grow max, supplements and cut-offs; hold free areas and deeming thresholds flat in real terms.
 */
export function getAgePensionParamsWithPensionGrowth(isCouple, isHomeowner, pensionRealGrowthMultiplier = 1, asAt = null) {
    const flat = getAgePensionParams(isCouple, isHomeowner, 1, 1, asAt);
    if (!Number.isFinite(pensionRealGrowthMultiplier) || pensionRealGrowthMultiplier === 1) {
        return flat;
    }
    const grown = getAgePensionParams(isCouple, isHomeowner, pensionRealGrowthMultiplier, 1, asAt);
    return {
        ...grown,
        assetFreeThreshold: flat.assetFreeThreshold,
        incomeFreeThreshold: flat.incomeFreeThreshold,
        lowDeemThresh: flat.lowDeemThresh,
    };
}

// Helper function to calculate Superannuation Guarantee contributions
export function calculateSGContributions(annualSalary, sgRate = FinancialConfig.SUPERANNUATION_GUARANTEE.RATE) {
    if (annualSalary <= 0) return 0;
    
    // SG is calculated on ordinary time earnings up to the maximum quarterly base
    const maxAnnualSGBase = FinancialConfig.SUPERANNUATION_GUARANTEE.MAXIMUM_QUARTERLY_BASE * 4;
    const sgBase = Math.min(annualSalary, maxAnnualSGBase);
    
    return sgBase * sgRate;
}

// Helper function to calculate available concessional contribution cap (including carry-forward)
export function calculateAvailableConcessionalCap(totalSuperBalance, carryForwardBalances = []) {
    const baseCap = FinancialConfig.CONTRIBUTION_CAPS.CONCESSIONAL;
    
    // Calculate total balance to determine eligibility for carry-forward
    const totalBalance = totalSuperBalance || 0;
    
    // Carry-forward eligibility is based on prior 30 June TSB being under $500,000.
    if (totalBalance >= FinancialConfig.TSB_THRESHOLDS.CARRY_FORWARD_CUTOFF) {
        return baseCap;
    }
    
    // Calculate available carry-forward (up to 5 years)
    const maxCarryForwardYears = FinancialConfig.CONTRIBUTION_CAPS.CARRY_FORWARD_YEARS;
    const availableCarryForward = carryForwardBalances
        .slice(0, maxCarryForwardYears)
        .reduce((sum, balance) => sum + Math.max(0, balance), 0);
    
    return baseCap + availableCarryForward;
}

// Helper function to calculate available non-concessional contribution cap
export function calculateAvailableNonConcessionalCap(totalSuperBalance) {
    const baseCap = FinancialConfig.CONTRIBUTION_CAPS.NON_CONCESSIONAL;
    const threshold = FinancialConfig.CONTRIBUTION_CAPS.TOTAL_BALANCE_THRESHOLD;
    
    // Previous-30-June TSB at or above the general TBC removes the annual cap.
    if (totalSuperBalance >= threshold) {
        return 0;
    }
    
    // The annual cap itself is not pro-rated to the gap below the TBC.
    return baseCap;
}

// Helper function to calculate Division 293 tax
// ATO: compare Division 293 income plus low-tax contributions with the threshold,
// then tax the lesser of the contributions and the combined excess.
export function calculateDivision293Tax(totalIncome, concessionalContributions) {
    return calculateDivision293Detail(totalIncome, concessionalContributions).div293Tax;
}

/** Full Division 293 breakdown — single source for UI, export, and SimulationEngine. */
export function calculateDivision293Detail(incomeForSurcharge, concessionalContributions) {
    const config = FinancialConfig.DIVISION_293;
    const contributions = Math.max(0, Number(concessionalContributions) || 0);
    const combinedIncomeAndContributions = Math.max(0, Number(incomeForSurcharge) || 0) + contributions;
    const excessIncome = Math.max(0, combinedIncomeAndContributions - config.INCOME_THRESHOLD);
    const taxableAmount = Math.min(contributions, excessIncome);
    const div293Tax = taxableAmount * config.TAX_RATE;
    return {
        incomeForSurcharge,
        combinedIncomeAndContributions,
        excessIncome,
        taxableAmount,
        div293Tax,
        isExempt: combinedIncomeAndContributions <= config.INCOME_THRESHOLD,
        marginalRate: contributions > 0
            ? (taxableAmount / contributions) * 100
            : 0,
        effectiveTaxRate: contributions > 0
            ? (div293Tax / contributions) * 100
            : 0,
    };
}

/**
 * Estimate Division 296 earnings from TSB movement (rough proxy only).
 */
export function estimateDivision296EarningsFromTsb(tsbStart, tsbEnd, contributions = 0, benefitsPaid = 0) {
    return tsbEnd - tsbStart - (contributions - benefitsPaid);
}

/**
 * TSB used for proportion / threshold tests under Division 296 rules.
 * 2026-27 transitional year: end-of-year TSB only. Later years: greater of start and end.
 */
export function division296TsbForProportion(tsbStart, tsbEnd, financialYear = null, configOverride = null) {
    const cfg = configOverride || FinancialConfig.DIVISION_296;
    const fy = financialYear || FinancialConfig._metadata?.financialYear;
    if (fy === cfg.FIRST_YEAR_FINANCIAL_YEAR || fy === '2026-27') {
        return tsbEnd;
    }
    if (tsbStart == null || Number.isNaN(tsbStart)) return tsbEnd;
    return Math.max(tsbStart, tsbEnd);
}

/** Map division296 block from resolved JSON rates (SSOT with financial-rates.json). */
export function mapDivision296FromJson(jsonData) {
    return {
        ENABLED: jsonData.division296?.enabled ?? FALLBACK_CONFIG.DIVISION_296.ENABLED,
        THRESHOLD: jsonData.division296?.largeSuperBalanceThreshold
            ?? jsonData.division296?.threshold
            ?? FALLBACK_CONFIG.DIVISION_296.THRESHOLD,
        VERY_LARGE_THRESHOLD: jsonData.division296?.veryLargeSuperBalanceThreshold
            ?? FALLBACK_CONFIG.DIVISION_296.VERY_LARGE_THRESHOLD,
        TAX_RATE: jsonData.division296?.taxRateLarge
            ?? jsonData.division296?.taxRate
            ?? FALLBACK_CONFIG.DIVISION_296.TAX_RATE,
        ADDITIONAL_RATE_ABOVE_VLSBT: jsonData.division296?.taxRateVeryLarge
            ?? FALLBACK_CONFIG.DIVISION_296.ADDITIONAL_RATE_ABOVE_VLSBT,
        EFFECTIVE_DATE: jsonData.division296?.effectiveFrom
            ?? FALLBACK_CONFIG.DIVISION_296.EFFECTIVE_DATE,
        LSBT_INDEX_INCREMENT: jsonData.division296?.lsbtIndexIncrement
            ?? FALLBACK_CONFIG.DIVISION_296.LSBT_INDEX_INCREMENT,
        VLSBT_INDEX_INCREMENT: jsonData.division296?.vlsbtIndexIncrement
            ?? FALLBACK_CONFIG.DIVISION_296.VLSBT_INDEX_INCREMENT,
        RELEASE_AUTHORITY_LIMIT: jsonData.division296?.releaseAuthorityLimit
            ?? FALLBACK_CONFIG.DIVISION_296.RELEASE_AUTHORITY_LIMIT,
        FIRST_YEAR_FINANCIAL_YEAR: jsonData.division296?.firstYearFinancialYear
            ?? FALLBACK_CONFIG.DIVISION_296.FIRST_YEAR_FINANCIAL_YEAR,
        EARNINGS_METHOD: jsonData.division296?.earningsMethod
            ?? FALLBACK_CONFIG.DIVISION_296.EARNINGS_METHOD,
    };
}

/** Store raw financial-rates.json (with schedules) for FY-resolved lookups in simulations. */
export function setRatesJsonRoot(json) {
    _ratesJsonRoot = json && typeof json === 'object' ? json : null;
}

export function getRatesJsonRoot() {
    return _ratesJsonRoot;
}

/** Prior indexed general TBCs — treat as stale defaults, not intentional overrides. */
export const HISTORICAL_GENERAL_TRANSFER_BALANCE_CAPS = Object.freeze([
    1600000, 1700000, 1900000, 2000000
]);

/** Map saved/demo/bridge general TBC onto the live statutory cap when stale. */
export function normalizeGeneralTransferBalanceCap(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0 || HISTORICAL_GENERAL_TRANSFER_BALANCE_CAPS.includes(n)) {
        return FinancialConfig.TRANSFER_BALANCE_CAP;
    }
    return n;
}

/**
 * Transfer balance cap for a simulation calendar year (1 July FY boundary).
 * Respects an explicit user override when it differs from the boot-hydrated default.
 * Saved values that merely match a prior general cap are not treated as overrides
 * (otherwise localStorage from 2025-26 locks every year at $2.0M after indexation).
 */
export function resolveTransferBalanceCapForCalendarYear(calendarYear, userOverride = null) {
    const bootDefault = FinancialConfig.TRANSFER_BALANCE_CAP;
    const override = normalizeGeneralTransferBalanceCap(userOverride);
    const hasExplicitOverride =
        Number.isFinite(Number(userOverride)) &&
        Number(userOverride) > 0 &&
        override === Number(userOverride) &&
        override !== bootDefault;

    if (hasExplicitOverride) {
        return override;
    }

    if (!_ratesJsonRoot?.schedules?.length) {
        return bootDefault;
    }

    const year = Number(calendarYear);
    const asAt = new Date(Date.UTC(Number.isFinite(year) ? year : 1980, 6, 1));
    const resolved = resolveFinancialYearRates(_ratesJsonRoot, asAt);
    return resolved.transferBalanceCap ?? bootDefault;
}

/** Division 296 config for a simulation date — resolves active FY schedule when available. */
export function resolveDivision296ConfigAt(asAt = new Date()) {
    if (!_ratesJsonRoot?.schedules) {
        return FinancialConfig.DIVISION_296;
    }
    return mapDivision296FromJson(resolveFinancialYearRates(_ratesJsonRoot, asAt));
}

/**
 * Member-level Division 296 surcharge on accumulation earnings (Advanced / SimulationEngine).
 * Delegates to calculateDivision296Tax — same formula as SMSF tab and widget.
 */
export function calculateDivision296MemberAccumulationSurcharge(memberEarnings, memberTsbEnd, options = {}) {
    const {
        asAtDate = null,
        tsbStart = null,
        simulationCalendarYear = null,
    } = options;

    let asAt = asAtDate;
    if (!asAt && simulationCalendarYear != null) {
        asAt = new Date(Date.UTC(simulationCalendarYear, 6, 1));
    }

    const cfg = asAt ? resolveDivision296ConfigAt(asAt) : FinancialConfig.DIVISION_296;
    if (!cfg.ENABLED) return 0;
    if (!memberTsbEnd || memberTsbEnd <= 0 || memberEarnings <= 0) return 0;

    const fy = asAt
        ? australianFinancialYearLabel(asAt)
        : (FinancialConfig._metadata?.financialYear || null);

    return calculateDivision296Tax(
        tsbStart ?? memberTsbEnd,
        memberTsbEnd,
        0,
        0,
        {
            division296Earnings: memberEarnings,
            financialYear: fy,
            configOverride: cfg,
        }
    ).taxAmount;
}

/**
 * Calculate Division 296 tax for a given financial year.
 *
 * @param {number} tsbStart - TSB at start of financial year ($)
 * @param {number} tsbEnd - TSB at end of financial year ($)
 * @param {number} contributions - Concessional + non-concessional contributions ($)
 * @param {number} benefitsPaid - Withdrawals / benefits paid ($)
 * @param {object} [options]
 * @param {number|null} [options.division296Earnings] - Reported Division 296 fund earnings (preferred)
 * @param {boolean} [options.useTsbProxy=false] - Force TSB-movement estimate instead of reported earnings
 * @param {string|null} [options.financialYear] - e.g. '2026-27' for transitional TSB test rules
 * @param {number|null} [options.tsbForProportion] - Override TSB used in proportion formula
 */
export function calculateDivision296Tax(
    tsbStart,
    tsbEnd,
    contributions = 0,
    benefitsPaid = 0,
    options = {}
) {
    const config = options.configOverride || FinancialConfig.DIVISION_296;
    const threshold = config.THRESHOLD;
    const veryLargeThreshold = config.VERY_LARGE_THRESHOLD;
    const {
        division296Earnings = null,
        useTsbProxy = false,
        financialYear = null,
        tsbForProportion = null,
        earningsSource: earningsSourceOverride = null,
        planningMode = false,
    } = options;

    const empty = (superEarnings = 0, earningsSource = 'none') => ({
        taxAmount: 0,
        taxLargeTier: 0,
        taxVeryLargeTier: 0,
        proportion: 0,
        proportionVeryLarge: 0,
        superEarnings,
        taxableEarnings: 0,
        isLiable: false,
        threshold,
        veryLargeThreshold,
        tsbForProportion: tsbForProportion ?? division296TsbForProportion(tsbStart, tsbEnd, financialYear, config),
        earningsSource,
    });

    if (!config.ENABLED && !planningMode) {
        return empty(0, 'disabled');
    }

    const tsbTest = tsbForProportion ?? division296TsbForProportion(tsbStart, tsbEnd, financialYear, config);
    const hasReportedEarnings = !useTsbProxy
        && division296Earnings != null
        && division296Earnings !== ''
        && !Number.isNaN(Number(division296Earnings));

    const superEarnings = hasReportedEarnings
        ? Number(division296Earnings)
        : estimateDivision296EarningsFromTsb(tsbStart, tsbEnd, contributions, benefitsPaid);

    const earningsSource = hasReportedEarnings
        ? (earningsSourceOverride || 'reported')
        : 'tsb_proxy';

    if (superEarnings <= 0 || tsbTest <= 0) {
        return empty(superEarnings, earningsSource);
    }

    const proportion = tsbTest > threshold ? (tsbTest - threshold) / tsbTest : 0;
    const proportionVeryLarge = tsbTest > veryLargeThreshold
        ? (tsbTest - veryLargeThreshold) / tsbTest
        : 0;

    if (proportion <= 0) {
        return { ...empty(superEarnings, earningsSource), tsbForProportion: tsbTest };
    }

    const taxLargeTier = superEarnings * proportion * config.TAX_RATE;
    const taxVeryLargeTier = proportionVeryLarge > 0
        ? superEarnings * proportionVeryLarge * config.ADDITIONAL_RATE_ABOVE_VLSBT
        : 0;
    const taxAmount = taxLargeTier + taxVeryLargeTier;
    const taxableEarnings = superEarnings * proportion
        + (proportionVeryLarge > 0 ? superEarnings * proportionVeryLarge : 0);

    return {
        taxAmount,
        taxLargeTier,
        taxVeryLargeTier,
        proportion,
        proportionVeryLarge,
        superEarnings,
        taxableEarnings,
        isLiable: true,
        threshold,
        veryLargeThreshold,
        tsbForProportion: tsbTest,
        earningsSource,
    };
}

// Low Income Super Tax Offset (LISTO) — refunds contributions tax for eligible low incomes
export function calculateLISTO(adjustedTaxableIncome, concessionalContributions) {
    const config = FinancialConfig.CONTRIBUTION_CAPS.LISTO;

    if (!concessionalContributions || concessionalContributions <= 0) {
        return {
            amount: 0,
            eligible: false,
            reason: 'No concessional contributions in the year'
        };
    }

    const ati = Number(adjustedTaxableIncome) || 0;
    if (ati > config.MAX_INCOME_THRESHOLD) {
        return {
            amount: 0,
            eligible: false,
            reason: `Adjusted taxable income ($${ati.toLocaleString()}) exceeds $${config.MAX_INCOME_THRESHOLD.toLocaleString()}`
        };
    }

    const contributionsTax = concessionalContributions * config.CONTRIBUTIONS_TAX_RATE;
    const amount = Math.min(config.MAX_OFFSET, contributionsTax);

    return {
        amount: Math.round(amount),
        eligible: amount > 0,
        reason: null
    };
}

// Helper function to calculate Low Income Tax Offset (LITO)
export function calculateLITO(taxableIncome, asAt = null) {
    const config = resolveFinancialConfigAsAt(asAt).TAX_OFFSETS.LITO;
    
    if (taxableIncome <= config.THRESHOLD_1) {
        // Full offset up to first threshold
        return config.MAX_OFFSET;
    } else if (taxableIncome <= config.THRESHOLD_2) {
        // Offset reduces between first and second threshold
        const reduction = (taxableIncome - config.THRESHOLD_1) * 0.05;
        return Math.max(0, config.MAX_OFFSET - reduction);
    } else if (taxableIncome <= config.THRESHOLD_3) {
        // Offset reduces to zero between second and third threshold
        return Math.max(0, 325 - (taxableIncome - config.THRESHOLD_2) * 0.015);
    } else {
        // No offset above third threshold
        return 0;
    }
}

// Helper function to calculate Seniors Tax Offset
export function calculateSeniorsTaxOffset(taxableIncome, isCouple = false, age = 67, illnessSeparated = false, asAt = null) {
    const resolvedConfig = resolveFinancialConfigAsAt(asAt);
    if (age < resolvedConfig.AGE_THRESHOLDS.AGE_PENSION_AGE) {
        return 0;
    }
    
    const config = resolvedConfig.TAX_OFFSETS.SENIORS;
    const seniorConfig = illnessSeparated
        ? config.ILLNESS_SEPARATED
        : (isCouple ? config.COUPLE : config.SINGLE);
    
    if (taxableIncome <= seniorConfig.THRESHOLD_1) {
        // Full offset up to first threshold
        return Math.ceil(seniorConfig.MAX_OFFSET);
    } else if (taxableIncome <= seniorConfig.THRESHOLD_2) {
        // Offset reduces between first and second threshold
        const reduction = (taxableIncome - seniorConfig.THRESHOLD_1) * 0.125;
        return Math.ceil(Math.max(0, seniorConfig.MAX_OFFSET - reduction));
    } else {
        // No offset above second threshold
        return 0;
    }
}

// Helper function to calculate Spouse Contribution Tax Offset
export function calculateSpouseContributionOffset(contributionAmount, spouseIncome) {
    const config = FinancialConfig.TAX_OFFSETS.SPOUSE_CONTRIBUTION;
    
    // Maximum contribution that can attract offset is $3,000
    const eligibleContribution = Math.min(contributionAmount, 3000);
    
    if (spouseIncome <= config.THRESHOLD_1) {
        // Full offset (18% of contribution)
        return eligibleContribution * 0.18;
    } else if (spouseIncome <= config.THRESHOLD_2) {
        // Offset reduces between thresholds
        const reduction = (spouseIncome - config.THRESHOLD_1) * 0.06;
        const offsetRate = Math.max(0, 0.18 - reduction / eligibleContribution);
        return eligibleContribution * offsetRate;
    } else {
        // No offset above second threshold
        return 0;
    }
}

// Helper function to verify CGT discount for superannuation context
export function calculateSuperCGTTax(capitalGain, isLongTerm = true) {
    const config = FinancialConfig.SUPER_TAX_RATES;
    
    if (!isLongTerm) {
        // Short-term capital gains: full tax rate
        return capitalGain * config.ACCUMULATION_EARNINGS;
    } else {
        // Long-term capital gains: apply CGT discount
        // In super context: 1/3 discount reduces 15% tax to 10% effective rate
        return capitalGain * config.CGT_DISCOUNT_RATE;
    }
}

// Helper function to calculate minimum pension withdrawal requirement
export function calculateMinimumPensionWithdrawal(pensionBalance, age) {
    return pensionBalance * getMinimumWithdrawalRate(age);
}

// Helper function to check work test requirements
export function checkWorkTestRequirements(age, contributionType = 'concessional', workTestMet = false) {
    const config = FinancialConfig.WORK_TEST;

    // Annual model: funds can generally accept SG, salary sacrifice and NCCs until age 75.
    // Since 1 July 2022 the work test only remains relevant to claiming a deduction for a
    // personal contribution from age 67 to 74; it does not gate SG/salary sacrifice/NCC.
    if (age >= 75) {
        return { required: false, passed: false, reason: 'Age limit exceeded for contributions' };
    }

    if (contributionType !== 'personalDeductible' || age < config.AGE_THRESHOLD) {
        return { required: false, passed: true, reason: 'No work test required for this contribution' };
    }

    return {
        required: true,
        passed: workTestMet,
        reason: workTestMet ? 'Work test satisfied' : 'Work test required for personal deduction'
    };
}

// Helper function to calculate bring-forward rule eligibility and limits
export function calculateBringForwardRule(totalSuperBalance, proposedContribution) {
    const config = FinancialConfig.BRING_FORWARD_RULE;
    const tsbConfig = FinancialConfig.TSB_THRESHOLDS;
    
    let maximumContribution;
    let yearsToUse;
    if (totalSuperBalance >= tsbConfig.BRING_FORWARD_ONE_YEAR) {
        maximumContribution = 0;
        yearsToUse = 0;
    } else if (totalSuperBalance >= tsbConfig.BRING_FORWARD_TWO_YEAR) {
        maximumContribution = FinancialConfig.CONTRIBUTION_CAPS.NON_CONCESSIONAL;
        yearsToUse = 1;
    } else if (totalSuperBalance >= tsbConfig.BRING_FORWARD_TRIGGER) {
        maximumContribution = FinancialConfig.CONTRIBUTION_CAPS.NON_CONCESSIONAL * 2;
        yearsToUse = 2;
    } else {
        maximumContribution = config.MAXIMUM_CONTRIBUTION;
        yearsToUse = config.YEARS_TO_USE;
    }

    if (maximumContribution === 0) {
        return {
            applicable: true,
            maximumContribution: 0,
            yearsToUse,
            reason: 'Total super balance is at or above the non-concessional contribution cutoff'
        };
    }
    
    // Check if proposed contribution triggers bring-forward rule
    if (proposedContribution > FinancialConfig.CONTRIBUTION_CAPS.NON_CONCESSIONAL) {
        return {
            applicable: true,
            maximumContribution,
            yearsToUse,
            reason: 'Proposed contribution triggers bring-forward rule'
        };
    }
    
    // Standard non-concessional cap applies
    return {
        applicable: false,
        maximumContribution: FinancialConfig.CONTRIBUTION_CAPS.NON_CONCESSIONAL,
        reason: 'Standard non-concessional cap applies'
    };
}

// Helper function to check total super balance thresholds
export function checkTSBThresholds(totalSuperBalance) {
    const config = FinancialConfig.TSB_THRESHOLDS;
    
    return {
        nonConcessionalContributionsAllowed: totalSuperBalance < config.NON_CONCESSIONAL_CUTOFF,
        bringForwardRuleTriggers: totalSuperBalance >= config.BRING_FORWARD_TRIGGER,
        carryForwardAvailable: totalSuperBalance < config.CARRY_FORWARD_CUTOFF,
        governmentCoContributionEligible: totalSuperBalance < config.GOVERNMENT_CO_CONTRIBUTION,
        thresholds: {
            nonConcessionalCutoff: config.NON_CONCESSIONAL_CUTOFF,
            bringForwardTrigger: config.BRING_FORWARD_TRIGGER,
            carryForwardCutoff: config.CARRY_FORWARD_CUTOFF,
            governmentCoContribution: config.GOVERNMENT_CO_CONTRIBUTION
        }
    };
}

// === SMSF-Specific Helper Functions ===

// Helper function to calculate SMSF trustee fees
export function calculateSMSFTrusteeFees(balance, trusteeType = 'individual') {
    const config = FinancialConfig.SMSF.FEES.TRUSTEE_FEES;
    const trusteeConfig = config[trusteeType.toUpperCase()] || config.INDIVIDUAL;
    
    // Calculate fee as fixed amount plus percentage of balance
    const fixedFee = trusteeConfig.FIXED;
    const percentageFee = balance * trusteeConfig.PERCENTAGE;
    const totalFee = fixedFee + percentageFee;
    
    // Apply maximum limit
    return Math.min(totalFee, trusteeConfig.MAXIMUM);
}

// Helper function to calculate SMSF audit costs
export function calculateSMSFAuditCosts(complexity = 'standard') {
    const config = FinancialConfig.SMSF.FEES.AUDIT_COSTS;
    
    switch (complexity.toLowerCase()) {
        case 'complex':
            return config.COMPLEX;
        case 'standard':
        default:
            return config.STANDARD;
    }
}

// Helper function to calculate total SMSF compliance costs
export function calculateSMSFComplianceCosts() {
    const config = FinancialConfig.SMSF.FEES.COMPLIANCE_COSTS;
    
    return config.ANNUAL_RETURN + 
           config.BOOKKEEPING + 
           config.ADMINISTRATION + 
           config.SOFTWARE_LICENSES;
}

// Helper function to calculate SMSF investment management fees by asset type
export function calculateSMSFInvestmentFees(assetType, balance, transactionVolume = 0) {
    const config = FinancialConfig.SMSF.FEES.INVESTMENT_MANAGEMENT;
    const assetConfig = config[assetType.toUpperCase()];
    
    if (!assetConfig) {
        return 0; // Unknown asset type
    }
    
    let totalFees = 0;
    
    switch (assetType.toLowerCase()) {
        case 'direct_shares':
            totalFees = balance * (assetConfig.BROKERAGE + assetConfig.RESEARCH);
            // Add transaction costs if provided
            if (transactionVolume > 0) {
                totalFees += Math.min(transactionVolume * assetConfig.BROKERAGE, assetConfig.MAXIMUM_PER_TRADE);
            }
            break;
            
        case 'managed_funds':
            totalFees = balance * assetConfig.MANAGEMENT_FEE;
            // Add buy/sell spread if applicable
            if (transactionVolume > 0) {
                totalFees += transactionVolume * assetConfig.BUY_SELL_SPREAD;
            }
            break;
            
        case 'property':
            totalFees = balance * (assetConfig.MANAGEMENT_FEE + assetConfig.MAINTENANCE_RESERVE);
            // Add annual valuation cost
            totalFees += assetConfig.VALUATION_COST;
            break;
            
        case 'cash_term_deposits':
            totalFees = balance * assetConfig.MANAGEMENT_FEE;
            if (transactionVolume > 0) {
                totalFees += assetConfig.TRANSACTION_COST;
            }
            break;
    }
    
    return totalFees;
}

// Helper function to calculate SMSF insurance costs
export function calculateSMSFInsuranceCosts(insuranceType, sumInsured, age = 50, gender = 'male') {
    const config = FinancialConfig.SMSF.FEES.INSURANCE;
    const insuranceConfig = config[insuranceType.toUpperCase()];
    
    if (!insuranceConfig) {
        return 0; // Unknown insurance type
    }
    
    // Validate sum insured within limits
    const validSumInsured = Math.max(
        insuranceConfig.MINIMUM_SUM,
        Math.min(sumInsured, insuranceConfig.MAXIMUM_SUM)
    );
    
    // Calculate base premium
    let premium = validSumInsured * insuranceConfig.BASE_RATE;
    
    // Apply age and gender adjustments (simplified model)
    const ageFactor = Math.max(0.5, Math.min(2.0, 1 + (age - 50) * 0.02));
    const genderFactor = gender.toLowerCase() === 'female' ? 1.1 : 1.0;
    
    return premium * ageFactor * genderFactor;
}

// Helper function to validate LRBA compliance
export function validateLRBACompliance(propertyValue, loanAmount, loanTerm, hasRelatedPartyRestrictions = false) {
    const config = FinancialConfig.SMSF.BORROWING.LRBA;
    const results = {
        valid: true,
        errors: [],
        warnings: [],
        compliance: {}
    };
    
    // Check loan-to-value ratio
    const lvr = loanAmount / propertyValue;
    if (lvr > config.MAXIMUM_LOAN_TO_VALUE) {
        results.valid = false;
        results.errors.push(`LVR exceeds maximum allowed: ${(lvr * 100).toFixed(1)}% > ${(config.MAXIMUM_LOAN_TO_VALUE * 100)}%`);
    }
    
    // Check minimum deposit
    const depositRatio = (propertyValue - loanAmount) / propertyValue;
    if (depositRatio < config.MINIMUM_DEPOSIT) {
        results.valid = false;
        results.errors.push(`Insufficient deposit: ${(depositRatio * 100).toFixed(1)}% < ${(config.MINIMUM_DEPOSIT * 100)}%`);
    }
    
    // Check loan term
    if (loanTerm > config.MAXIMUM_LOAN_TERM) {
        results.valid = false;
        results.errors.push(`Loan term exceeds maximum: ${loanTerm} years > ${config.MAXIMUM_LOAN_TERM} years`);
    }
    
    // Check related party restrictions
    if (hasRelatedPartyRestrictions && !config.RELATED_PARTY_RESTRICTIONS) {
        results.warnings.push('Related party restrictions may apply');
    }
    
    results.compliance = {
        lvr,
        depositRatio,
        loanTerm,
        maxLvrAllowed: config.MAXIMUM_LOAN_TO_VALUE,
        minDepositRequired: config.MINIMUM_DEPOSIT,
        maxLoanTerm: config.MAXIMUM_LOAN_TERM
    };
    
    return results;
}

// Helper function to validate in-house asset compliance
export function validateInHouseAssetCompliance(totalAssets, inHouseAssets) {
    const config = FinancialConfig.SMSF.COMPLIANCE.IN_HOUSE_ASSETS;
    const inHousePercentage = inHouseAssets / totalAssets;
    
    const results = {
        valid: inHousePercentage <= config.MAXIMUM_PERCENTAGE,
        inHousePercentage,
        maximumAllowed: config.MAXIMUM_PERCENTAGE,
        excessAmount: Math.max(0, inHouseAssets - (totalAssets * config.MAXIMUM_PERCENTAGE)),
        requiresCorrection: inHousePercentage > config.MAXIMUM_PERCENTAGE
    };
    
    if (!results.valid) {
        results.error = `In-house assets exceed 5% limit: ${(inHousePercentage * 100).toFixed(2)}% > ${(config.MAXIMUM_PERCENTAGE * 100)}%`;
        results.penaltyApplicable = config.PENALTIES_APPLY;
    }
    
    return results;
}

// Helper function to calculate SMSF valuation costs
export function calculateSMSFValuationCosts(assets) {
    const config = FinancialConfig.SMSF.VALUATION;
    let totalCost = 0;
    
    // Calculate costs by asset type
    if (assets.realEstate && assets.realEstate > 0) {
        const realEstateConfig = config.REAL_ESTATE;
        if (assets.realEstate >= config.REQUIREMENTS.PROFESSIONAL_VALUATION_THRESHOLD) {
            totalCost += realEstateConfig.VALUATION_COST_RANGE.TYPICAL;
        }
    }
    
    if (assets.collectibles && assets.collectibles > 0) {
        const collectiblesConfig = config.COLLECTIBLES;
        totalCost += collectiblesConfig.VALUATION_COST_RANGE.TYPICAL;
    }
    
    // Shares and bonds typically don't incur valuation costs
    // (uses market prices)
    
    return totalCost;
}

// Helper function to calculate total SMSF costs
export function calculateTotalSMSFCosts(balance, trusteeType = 'individual', assetTypes = {}, complexity = 'standard') {
    const trusteeFees = calculateSMSFTrusteeFees(balance, trusteeType);
    const auditCosts = calculateSMSFAuditCosts(complexity);
    const complianceCosts = calculateSMSFComplianceCosts();
    const valuationCosts = calculateSMSFValuationCosts(assetTypes);
    
    // Calculate investment management fees for each asset type
    let investmentFees = 0;
    Object.entries(assetTypes).forEach(([assetType, balance]) => {
        if (balance > 0) {
            investmentFees += calculateSMSFInvestmentFees(assetType, balance);
        }
    });
    
    return {
        trusteeFees,
        auditCosts,
        complianceCosts,
        valuationCosts,
        investmentFees,
        total: trusteeFees + auditCosts + complianceCosts + valuationCosts + investmentFees,
        breakdown: {
            trusteeFees,
            auditCosts,
            complianceCosts,
            valuationCosts,
            investmentFees
        }
    };
}

// Main export - dynamically loaded configuration
let _loadedConfig = null;
let _isLoading = false;
let _loadPromise = null;

/**
 * Get financial configuration - loads from JSON or returns cached
 * @param {boolean} forceRefresh - Force reload from JSON
 * @returns {Promise<Object>} Financial configuration
 */
export async function getFinancialConfig(forceRefresh = false) {
    // Return cached if available
    if (_loadedConfig && !forceRefresh) {
        return _loadedConfig;
    }

    // If already loading, wait for that promise
    if (_isLoading && _loadPromise) {
        return _loadPromise;
    }

    // Start loading
    _isLoading = true;
    _loadPromise = (async () => {
        try {
            const jsonData = await FinancialDataLoader.loadRates(new Date(), forceRefresh);
            setRatesJsonRoot(FinancialDataLoader.getRawRatesJson());
            
            if (jsonData && jsonData._metadata?.source !== 'hardcoded_fallback') {
                // Map JSON structure to our config structure and sync live export
                _loadedConfig = mapJSONToConfig(jsonData);
                deepMergeInto(FALLBACK_CONFIG, _loadedConfig);
                _loadedConfig = FALLBACK_CONFIG;
                console.info('✓ Financial rates loaded from JSON source');
            } else {
                // Use fallback
                _loadedConfig = FALLBACK_CONFIG;
                console.info('⚠ Using hardcoded fallback rates');
            }
            
            return _loadedConfig;
        } catch (error) {
            console.error('Failed to load financial config:', error);
            _loadedConfig = FALLBACK_CONFIG;
            return _loadedConfig;
        } finally {
            _isLoading = false;
        }
    })();

    return _loadPromise;
}

/**
 * Map JSON data structure to FinancialConfig structure
 * @param {Object} jsonData - Data from JSON file or API
 * @returns {Object} Mapped configuration
 */
/**
 * Deep-merge mapped config into the live FALLBACK object (and FinancialConfig export).
 */
function deepMergeInto(target, source) {
    for (const [key, value] of Object.entries(source)) {
        if (value === undefined) continue;
        if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
            if (key === 'AGE_PENSION' && target[key]) {
                mergeAgePensionInto(target[key], value);
                continue;
            }
            if (target[key] && typeof target[key] === 'object' && !Array.isArray(target[key])) {
                deepMergeInto(target[key], value);
            } else {
                target[key] = value;
            }
        } else {
            target[key] = value;
        }
    }
}

/** Keys owned by date-aware getters — never overwrite from financial-rates.json. */
const AGE_PENSION_DATE_MANAGED_KEYS = new Set([
    'MAX_PENSION',
    'ASSET_TEST',
    'INCOME_TEST',
    'SUPPLEMENTS',
    'RENT_ASSISTANCE',
    'DEEMING',
    'DEEMED_INCOME_RATES',
]);

function mergeAgePensionInto(targetAp, sourceAp) {
    for (const [key, value] of Object.entries(sourceAp)) {
        if (AGE_PENSION_DATE_MANAGED_KEYS.has(key)) continue;
        if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
            if (!targetAp[key]) targetAp[key] = {};
            deepMergeInto(targetAp[key], value);
        } else if (value !== undefined) {
            targetAp[key] = value;
        }
    }
}

/**
 * Apply financial-rates.json (or mapped config) to the shared FinancialConfig object.
 * Used by the widget bundle at build time and optionally after async load in the app.
 */
export function hydrateFinancialConfigFromJson(jsonData) {
    if (!jsonData) return false;
    const mapped = mapJSONToConfig(jsonData);
    deepMergeInto(FALLBACK_CONFIG, mapped);
    if (jsonData.metadata) {
        FALLBACK_CONFIG._metadata = { ...jsonData.metadata };
    }
    _loadedConfig = FALLBACK_CONFIG;
    return true;
}

function mapJSONToConfig(jsonData) {
    // If it's already in our format (from fallback), return as-is
    if (jsonData.TRANSFER_BALANCE_CAP) {
        return jsonData;
    }

    // Map JSON structure to our internal structure (fallback first, then JSON overrides)
    return {
        ...FALLBACK_CONFIG,
        TRANSFER_BALANCE_CAP: jsonData.transferBalanceCap || 2100000,
        DEFINED_BENEFIT_INCOME_CAP: jsonData.definedBenefitIncomeCap || 131250,
        CGT_CAP_AMOUNT: jsonData.cgtCapAmount || 1935000,

        SUPER_TAX_RATES: {
            CONTRIBUTIONS_TAX: jsonData.superTaxRates?.contributionsTax || 0.15,
            ACCUMULATION_EARNINGS: jsonData.superTaxRates?.accumulationEarnings || 0.15,
            CGT_DISCOUNT_RATE: jsonData.superTaxRates?.cgtDiscountRate || 0.10,
            WITHDRAWAL_TAX_UNDER_60: jsonData.superTaxRates?.withdrawalTaxUnder60 || 0.15,
            WITHDRAWAL_TAX_OVER_60: jsonData.superTaxRates?.withdrawalTaxOver60 || 0.00,
            DIVISION_293_RATE: jsonData.superTaxRates?.division293Rate || 0.15
        },

        DIVISION_293: {
            INCOME_THRESHOLD: jsonData.division293?.incomeThreshold || 250000,
            TAX_RATE: jsonData.division293?.taxRate || 0.15
        },

        DIVISION_296: mapDivision296FromJson(jsonData),

        INCOME_TAX_BRACKETS: jsonData.incomeTaxBrackets?.resident?.map(bracket => ({
            min: bracket.min,
            max: bracket.max === null ? Infinity : bracket.max,
            rate: bracket.rate
        })) || FALLBACK_CONFIG.INCOME_TAX_BRACKETS,

        TAX_OFFSETS: {
            ...FALLBACK_CONFIG.TAX_OFFSETS,
            LITO: {
                ...FALLBACK_CONFIG.TAX_OFFSETS.LITO,
                MAX_OFFSET: jsonData.taxOffsets?.lito?.maximum ?? FALLBACK_CONFIG.TAX_OFFSETS.LITO.MAX_OFFSET,
                THRESHOLD_1: jsonData.taxOffsets?.lito?.threshold1
                    ?? jsonData.taxOffsets?.lito?.phaseOutStart
                    ?? FALLBACK_CONFIG.TAX_OFFSETS.LITO.THRESHOLD_1,
                THRESHOLD_2: jsonData.taxOffsets?.lito?.threshold2
                    ?? jsonData.taxOffsets?.lito?.phaseOutEnd
                    ?? FALLBACK_CONFIG.TAX_OFFSETS.LITO.THRESHOLD_2,
                THRESHOLD_3: jsonData.taxOffsets?.lito?.threshold3
                    ?? FALLBACK_CONFIG.TAX_OFFSETS.LITO.THRESHOLD_3,
            },
            SENIORS: {
                SINGLE: {
                    ...FALLBACK_CONFIG.TAX_OFFSETS.SENIORS.SINGLE,
                    MAX_OFFSET: jsonData.taxOffsets?.sapto?.single?.maximum ?? FALLBACK_CONFIG.TAX_OFFSETS.SENIORS.SINGLE.MAX_OFFSET,
                    THRESHOLD_1: jsonData.taxOffsets?.sapto?.single?.shadeOut ?? FALLBACK_CONFIG.TAX_OFFSETS.SENIORS.SINGLE.THRESHOLD_1,
                    THRESHOLD_2: jsonData.taxOffsets?.sapto?.single?.cutOut ?? FALLBACK_CONFIG.TAX_OFFSETS.SENIORS.SINGLE.THRESHOLD_2,
                },
                COUPLE: {
                    ...FALLBACK_CONFIG.TAX_OFFSETS.SENIORS.COUPLE,
                    MAX_OFFSET: jsonData.taxOffsets?.sapto?.couple?.maximum ?? FALLBACK_CONFIG.TAX_OFFSETS.SENIORS.COUPLE.MAX_OFFSET,
                    THRESHOLD_1: jsonData.taxOffsets?.sapto?.couple?.shadeOut ?? FALLBACK_CONFIG.TAX_OFFSETS.SENIORS.COUPLE.THRESHOLD_1,
                    THRESHOLD_2: jsonData.taxOffsets?.sapto?.couple?.cutOut ?? FALLBACK_CONFIG.TAX_OFFSETS.SENIORS.COUPLE.THRESHOLD_2,
                },
                ILLNESS_SEPARATED: {
                    ...FALLBACK_CONFIG.TAX_OFFSETS.SENIORS.ILLNESS_SEPARATED,
                    MAX_OFFSET: jsonData.taxOffsets?.sapto?.illnessSeparated?.maximum ?? FALLBACK_CONFIG.TAX_OFFSETS.SENIORS.ILLNESS_SEPARATED.MAX_OFFSET,
                    THRESHOLD_1: jsonData.taxOffsets?.sapto?.illnessSeparated?.shadeOut ?? FALLBACK_CONFIG.TAX_OFFSETS.SENIORS.ILLNESS_SEPARATED.THRESHOLD_1,
                    THRESHOLD_2: jsonData.taxOffsets?.sapto?.illnessSeparated?.cutOut ?? FALLBACK_CONFIG.TAX_OFFSETS.SENIORS.ILLNESS_SEPARATED.THRESHOLD_2,
                },
            },
            SPOUSE_CONTRIBUTION: {
                ...FALLBACK_CONFIG.TAX_OFFSETS.SPOUSE_CONTRIBUTION,
                MAX_OFFSET: jsonData.taxOffsets?.spouseContribution?.maximum ?? FALLBACK_CONFIG.TAX_OFFSETS.SPOUSE_CONTRIBUTION.MAX_OFFSET,
                THRESHOLD_1: jsonData.taxOffsets?.spouseContribution?.threshold1 ?? FALLBACK_CONFIG.TAX_OFFSETS.SPOUSE_CONTRIBUTION.THRESHOLD_1,
                THRESHOLD_2: jsonData.taxOffsets?.spouseContribution?.threshold2 ?? FALLBACK_CONFIG.TAX_OFFSETS.SPOUSE_CONTRIBUTION.THRESHOLD_2,
            }
        },

        INCOME_TAX: {
            ...FALLBACK_CONFIG.INCOME_TAX,
            MEDICARE_LEVY: jsonData.incomeTaxBrackets?.medicareLevy ?? FALLBACK_CONFIG.INCOME_TAX.MEDICARE_LEVY,
            MEDICARE_LEVY_THRESHOLDS: {
                ...FALLBACK_CONFIG.INCOME_TAX.MEDICARE_LEVY_THRESHOLDS,
                SINGLE: {
                    LOWER: jsonData.incomeTaxBrackets?.medicareLevyThresholds?.single?.lower
                        ?? FALLBACK_CONFIG.INCOME_TAX.MEDICARE_LEVY_THRESHOLDS.SINGLE.LOWER,
                    UPPER: jsonData.incomeTaxBrackets?.medicareLevyThresholds?.single?.upper
                        ?? FALLBACK_CONFIG.INCOME_TAX.MEDICARE_LEVY_THRESHOLDS.SINGLE.UPPER,
                },
                SAPTO_SINGLE: {
                    LOWER: jsonData.incomeTaxBrackets?.medicareLevyThresholds?.saptoSingle?.lower
                        ?? FALLBACK_CONFIG.INCOME_TAX.MEDICARE_LEVY_THRESHOLDS.SAPTO_SINGLE.LOWER,
                    UPPER: jsonData.incomeTaxBrackets?.medicareLevyThresholds?.saptoSingle?.upper
                        ?? FALLBACK_CONFIG.INCOME_TAX.MEDICARE_LEVY_THRESHOLDS.SAPTO_SINGLE.UPPER,
                },
                FAMILY: {
                    LOWER: jsonData.incomeTaxBrackets?.medicareLevyThresholds?.family?.lower
                        ?? FALLBACK_CONFIG.INCOME_TAX.MEDICARE_LEVY_THRESHOLDS.FAMILY.LOWER,
                    UPPER: jsonData.incomeTaxBrackets?.medicareLevyThresholds?.family?.upper
                        ?? FALLBACK_CONFIG.INCOME_TAX.MEDICARE_LEVY_THRESHOLDS.FAMILY.UPPER,
                },
                SAPTO_FAMILY: {
                    LOWER: jsonData.incomeTaxBrackets?.medicareLevyThresholds?.saptoFamily?.lower
                        ?? FALLBACK_CONFIG.INCOME_TAX.MEDICARE_LEVY_THRESHOLDS.SAPTO_FAMILY.LOWER,
                    UPPER: jsonData.incomeTaxBrackets?.medicareLevyThresholds?.saptoFamily?.upper
                        ?? FALLBACK_CONFIG.INCOME_TAX.MEDICARE_LEVY_THRESHOLDS.SAPTO_FAMILY.UPPER,
                },
                DEPENDANT_CHILD_LOWER: jsonData.incomeTaxBrackets?.medicareLevyThresholds?.dependantChildLower
                    ?? FALLBACK_CONFIG.INCOME_TAX.MEDICARE_LEVY_THRESHOLDS.DEPENDANT_CHILD_LOWER,
                DEPENDANT_CHILD_UPPER: jsonData.incomeTaxBrackets?.medicareLevyThresholds?.dependantChildUpper
                    ?? FALLBACK_CONFIG.INCOME_TAX.MEDICARE_LEVY_THRESHOLDS.DEPENDANT_CHILD_UPPER,
            },
            MEDICARE_LEVY_SURCHARGE: {
                ...FALLBACK_CONFIG.INCOME_TAX.MEDICARE_LEVY_SURCHARGE,
                SINGLE: jsonData.incomeTaxBrackets?.medicareLevySurchargeThresholds?.single
                    ?? FALLBACK_CONFIG.INCOME_TAX.MEDICARE_LEVY_SURCHARGE.SINGLE,
                FAMILY: jsonData.incomeTaxBrackets?.medicareLevySurchargeThresholds?.family
                    ?? FALLBACK_CONFIG.INCOME_TAX.MEDICARE_LEVY_SURCHARGE.FAMILY,
                DEPENDANT_CHILD_INCREMENT: jsonData.incomeTaxBrackets?.medicareLevySurchargeThresholds?.dependantChildIncrement
                    ?? FALLBACK_CONFIG.INCOME_TAX.MEDICARE_LEVY_SURCHARGE.DEPENDANT_CHILD_INCREMENT,
            },
        },

        SUPERANNUATION_GUARANTEE: {
            RATE: jsonData.superannuationGuarantee?.rate || 0.12,
            MAXIMUM_QUARTERLY_BASE: jsonData.superannuationGuarantee?.maximumQuarterlyBase || 67707.5,
            MINIMUM_THRESHOLD: jsonData.superannuationGuarantee?.minimumThreshold || 450,
            CONTRIBUTIONS_BASE: jsonData.superannuationGuarantee?.contributionsBase || 32500
        },

        CONTRIBUTION_CAPS: {
            ...FALLBACK_CONFIG.CONTRIBUTION_CAPS,
            CONCESSIONAL: jsonData.contributionCaps?.concessional || 32500,
            NON_CONCESSIONAL: jsonData.contributionCaps?.nonConcessional || 130000,
            CARRY_FORWARD_YEARS: jsonData.contributionCaps?.carryForwardYears || 5,
            TOTAL_BALANCE_THRESHOLD: jsonData.contributionCaps?.totalBalanceThreshold || 2100000,
            
            CO_CONTRIBUTION: {
                MAX_AMOUNT: jsonData.contributionCaps?.coContribution?.maxAmount || 500,
                MIN_INCOME_THRESHOLD: jsonData.contributionCaps?.coContribution?.minIncomeThreshold || 47488,
                MAX_INCOME_THRESHOLD: jsonData.contributionCaps?.coContribution?.maxIncomeThreshold || 62488,
                MATCHING_RATE: jsonData.contributionCaps?.coContribution?.matchingRate || 0.5
            },
            LISTO: {
                MAX_OFFSET: jsonData.contributionCaps?.listo?.maxOffset || 500,
                MIN_INCOME_THRESHOLD: jsonData.contributionCaps?.listo?.minIncomeThreshold || 37000,
                MAX_INCOME_THRESHOLD: jsonData.contributionCaps?.listo?.maxIncomeThreshold || 45000,
                CONTRIBUTIONS_TAX_RATE: jsonData.contributionCaps?.listo?.contributionsTaxRate || 0.15
            }
        },

        BRING_FORWARD_RULE: {
            TRIGGER_THRESHOLD: jsonData.bringForwardRule?.triggerThreshold || 2100000,
            MAXIMUM_CONTRIBUTION: jsonData.bringForwardRule?.maximumContribution || 390000,
            YEARS_TO_USE: jsonData.bringForwardRule?.yearsToUse || 3,
            MINIMUM_CONTRIBUTION: jsonData.bringForwardRule?.minimumContribution || 130000
        },

        TSB_THRESHOLDS: {
            NON_CONCESSIONAL_CUTOFF: jsonData.tsbThresholds?.nonConcessionalCutoff || 2100000,
            BRING_FORWARD_TRIGGER: jsonData.tsbThresholds?.bringForwardTrigger || 1840000,
            BRING_FORWARD_TWO_YEAR: jsonData.tsbThresholds?.bringForwardTwoYear || 1970000,
            BRING_FORWARD_ONE_YEAR: jsonData.tsbThresholds?.bringForwardOneYear || 2100000,
            CARRY_FORWARD_CUTOFF: jsonData.tsbThresholds?.carryForwardCutoff || 500000,
            GOVERNMENT_CO_CONTRIBUTION: jsonData.tsbThresholds?.governmentCoContribution || 2100000
        },

        // Rates/cut-offs/deeming stay on FALLBACK getters (20 Sep 2026 auto-switch).
        // JSON may still carry documentary agePension fields; mergeAgePensionInto skips them.
        AGE_PENSION: {
            WORK_BONUS: FALLBACK_CONFIG.AGE_PENSION.WORK_BONUS,
            GROWTH_RATE: FALLBACK_CONFIG.AGE_PENSION.GROWTH_RATE,
            ASSET_TAPER: jsonData.agePension?.assetTaper || 0.078,
        },

        MINIMUM_PENSION_STANDARDS: mapMinimumPensionStandards(jsonData),

        SMSF: {
            ...FALLBACK_CONFIG.SMSF,
            FEES: {
                ...FALLBACK_CONFIG.SMSF.FEES,
                TRUSTEE_FEES: {
                    INDIVIDUAL: {
                        ...FALLBACK_CONFIG.SMSF.FEES.TRUSTEE_FEES.INDIVIDUAL,
                        FIXED: jsonData.smsfFees?.trusteeFees?.individual?.fixed ?? FALLBACK_CONFIG.SMSF.FEES.TRUSTEE_FEES.INDIVIDUAL.FIXED,
                        PERCENTAGE: jsonData.smsfFees?.trusteeFees?.individual?.percentage ?? FALLBACK_CONFIG.SMSF.FEES.TRUSTEE_FEES.INDIVIDUAL.PERCENTAGE,
                        MAXIMUM: jsonData.smsfFees?.trusteeFees?.individual?.maximum ?? FALLBACK_CONFIG.SMSF.FEES.TRUSTEE_FEES.INDIVIDUAL.MAXIMUM,
                    },
                    CORPORATE: {
                        ...FALLBACK_CONFIG.SMSF.FEES.TRUSTEE_FEES.CORPORATE,
                        FIXED: jsonData.smsfFees?.trusteeFees?.corporate?.fixed ?? FALLBACK_CONFIG.SMSF.FEES.TRUSTEE_FEES.CORPORATE.FIXED,
                        PERCENTAGE: jsonData.smsfFees?.trusteeFees?.corporate?.percentage ?? FALLBACK_CONFIG.SMSF.FEES.TRUSTEE_FEES.CORPORATE.PERCENTAGE,
                        MAXIMUM: jsonData.smsfFees?.trusteeFees?.corporate?.maximum ?? FALLBACK_CONFIG.SMSF.FEES.TRUSTEE_FEES.CORPORATE.MAXIMUM,
                    },
                },
                AUDIT_COSTS: {
                    STANDARD: jsonData.smsfFees?.auditCosts?.standard ?? FALLBACK_CONFIG.SMSF.FEES.AUDIT_COSTS.STANDARD,
                    COMPLEX: jsonData.smsfFees?.auditCosts?.complex ?? FALLBACK_CONFIG.SMSF.FEES.AUDIT_COSTS.COMPLEX,
                    MINIMUM: jsonData.smsfFees?.auditCosts?.minimum ?? FALLBACK_CONFIG.SMSF.FEES.AUDIT_COSTS.MINIMUM,
                    MAXIMUM: jsonData.smsfFees?.auditCosts?.maximum ?? FALLBACK_CONFIG.SMSF.FEES.AUDIT_COSTS.MAXIMUM,
                },
                COMPLIANCE_COSTS: {
                    ANNUAL_RETURN: jsonData.smsfFees?.complianceCosts?.annualReturn ?? FALLBACK_CONFIG.SMSF.FEES.COMPLIANCE_COSTS.ANNUAL_RETURN,
                    BOOKKEEPING: jsonData.smsfFees?.complianceCosts?.bookkeeping ?? FALLBACK_CONFIG.SMSF.FEES.COMPLIANCE_COSTS.BOOKKEEPING,
                    ADMINISTRATION: jsonData.smsfFees?.complianceCosts?.administration ?? FALLBACK_CONFIG.SMSF.FEES.COMPLIANCE_COSTS.ADMINISTRATION,
                    SOFTWARE_LICENSES: jsonData.smsfFees?.complianceCosts?.softwareLicenses ?? FALLBACK_CONFIG.SMSF.FEES.COMPLIANCE_COSTS.SOFTWARE_LICENSES,
                },
            },
        },
        _metadata: { ...(jsonData.metadata || {}) },
    };
}

/**
 * Map minimum pension standards from JSON
 */
function mapMinimumPensionStandards(jsonData) {
    if (!jsonData.minimumPensionStandards) {
        return FALLBACK_CONFIG.MINIMUM_PENSION_STANDARDS;
    }

    const rates = jsonData.minimumPensionStandards.withdrawalRates || {};
    
    return {
        WITHDRAWAL_RATES: {
            UNDER_65: rates['under65'] || 0.04,
            '65-74': rates['65-74'] || 0.05,
            '75-79': rates['75-79'] || 0.06,
            '80-84': rates['80-84'] || 0.07,
            '85-89': rates['85-89'] || 0.09,
            '90-94': rates['90-94'] || 0.11,
            '95+': rates['95+'] || 0.14
        },
        MAXIMUM_WITHDRAWAL_RATE: jsonData.minimumPensionStandards.maximumWithdrawalRate || 0.10
    };
}

/**
 * Get synchronous config (uses cached or fallback)
 * Use this only when you can't use async
 */
export function getFinancialConfigSync() {
    if (_loadedConfig) {
        return _loadedConfig;
    }
    console.warn('⚠ Using synchronous fallback - config may not be up to date');
    return FALLBACK_CONFIG;
}

/**
 * Force refresh of financial configuration
 */
export async function refreshFinancialConfig() {
    _loadedConfig = null;
    return getFinancialConfig(true);
}

// Legacy export for backward compatibility - returns fallback immediately
export const FinancialConfig = FALLBACK_CONFIG;

// Export default for easy importing
export default {
    getFinancialConfig,
    getFinancialConfigSync,
    refreshFinancialConfig,
    FinancialConfig: FALLBACK_CONFIG
};


